/**
 * Implementation of LocalizationContext.
 */
class LocalizationContextImplementation {
    /**
     * Construct LocalizationContextImplementation via builder.
     * @param builder LocalizationContextBuilder
     */
    constructor(builder) {
        this._locale = builder.locale;
        this._defaultLocale = builder.defaultLocale;
        this._timeZone = builder.timeZone;
        this._i18nDomain = builder.i18nDomain;
    }
    /**
     * Get timeZone from localization context.
     * @returns timeZone.
     */
    getTimeZone() {
        return this._timeZone;
    }
    /**
     * Get locale from localization context.
     * @returns locale.
     */
    getLocale() {
        return this._locale;
    }
    /**
     * Get default locale from localization context.
     * @returns default locale.
     */
    getDefaultLocale() {
        return this._defaultLocale;
    }
    /**
     * Gets i18nDomain
     * @returns i18nDomain.
     */
    getI18nDomain() {
        return this._i18nDomain;
    }
    /**
     * Get all attributes from localization context.
     * @returns attributes.
     */
    asAttributesMap() {
        const result = new Map([
            [LOCALE_ATTRIBUTE, this._locale],
            [DEFAULT_LOCALE_ATTRIBUTE, this._defaultLocale],
            [TIME_ZONE_ATTRIBUTE, this._timeZone],
            [I18N_DOMAIN_ATTRIBUTE, this._i18nDomain],
        ]);
        return result;
    }
    toString() {
        let stringForm = 'LocalizationContext[';
        stringForm += `${LOCALE_ATTRIBUTE}=${this.getLocale()},`;
        stringForm += `${TIME_ZONE_ATTRIBUTE}=${this.getTimeZone()},`;
        stringForm += `${DEFAULT_LOCALE_ATTRIBUTE}=${this.getDefaultLocale()},`;
        stringForm += `${I18N_DOMAIN_ATTRIBUTE}=${this.getI18nDomain()}]`;
        return stringForm;
    }
}
const LOCALE_ATTRIBUTE = 'locale';
const DEFAULT_LOCALE_ATTRIBUTE = 'defaultLocale';
const TIME_ZONE_ATTRIBUTE = 'timeZone';
const I18N_DOMAIN_ATTRIBUTE = 'i18nDomain';

/**
 * Builder class for {@link LocalizationContextImplementation}.
 */
//TODO: Use options object instead of builder.
class LocalizationContextBuilder {
    constructor() {
        this._locale = LocalizationContextBuilder.DEFAULT_LOCALE;
        this._defaultLocale = undefined;
        this._timeZone = LocalizationContextBuilder.DEFAULT_TIME_ZONE;
        this._i18nDomain = '';
    }
    /**
     * Gets locale. Default value is OS locale.
     */
    get locale() {
        return this._locale;
    }
    /**
     * Gets default locale. Default value is undefined.
     */
    get defaultLocale() {
        return this._defaultLocale;
    }
    /**
     * Gets timeZone. Default value is set to local time zone.
     */
    get timeZone() {
        return this._timeZone;
    }
    /**
     * Gets i18nDomain. Default value is empty string.
     */
    get i18nDomain() {
        return this._i18nDomain;
    }
    /**
     * Sets locale on context.
     * @param locale
     * @returns a reference to this object with locale set
     */
    withLocale(locale) {
        try {
            // @ts-ignore
            this._locale = Intl.getCanonicalLocales(locale)[0];
        }
        catch (error) {
            console.error(`Provided Locale is invalid: locale=${locale}. Using default runtime locale: ${LocalizationContextBuilder.DEFAULT_LOCALE}`);
        }
        return this;
    }
    /**
     * Sets default locale.
     * @param defaultLocale
     * @returns a reference to this object with default locale set
     */
    withDefaultLocale(defaultLocale) {
        try {
            // @ts-ignore
            this._defaultLocale = Intl.getCanonicalLocales(defaultLocale)[0];
        }
        catch (error) {
            console.error(`Provided DefaultLocale is invalid: defaultLocale:${defaultLocale}.`);
        }
        return this;
    }
    /**
     * Sets i18nDomain.
     * @param i18nDomain
     * @returns a reference to this object with i18nDomain set
     */
    withI18nDomain(domain) {
        this._i18nDomain = domain;
        return this;
    }
    /**
     * Sets timeZone. TimeZone plain string is validated using Intl.
     * @param timeZone
     * @returns a reference to this object with timeZone set
     */
    withTimeZone(timeZone) {
        try {
            const dateTimeFormat = new Intl.DateTimeFormat(undefined, { timeZone });
            this._timeZone = dateTimeFormat.resolvedOptions().timeZone;
        }
        catch (error) {
            console.error(`Provided TimeZone is invalid: timeZone=${timeZone}. Using default TimeZone: ${LocalizationContextBuilder.DEFAULT_TIME_ZONE}`);
        }
        return this;
    }
    /**
     * Builds localization context.
     * @returns LocalizationContextImplementation
     */
    build() {
        return new LocalizationContextImplementation(this);
    }
}
LocalizationContextBuilder.DATE_TIME_OPTIONS = new Intl.DateTimeFormat().resolvedOptions();
LocalizationContextBuilder.DEFAULT_LOCALE = LocalizationContextBuilder.DATE_TIME_OPTIONS.locale;
LocalizationContextBuilder.DEFAULT_TIME_ZONE = LocalizationContextBuilder.DATE_TIME_OPTIONS.timeZone;

class CallbackLocalizationContextProvider {
    constructor(provideContext) {
        this.provideContext = provideContext;
    }
    getLocalizationContext() {
        return this.provideContext();
    }
}

/**
 * LocalizationContext for Amazon Retail.
 */
class RetailLocalizationContext extends LocalizationContextImplementation {
    /**
     * Construct RetailLocalizationContext via builder.
     * @param builder RetailLocalizationContextBuilder
     */
    constructor(builder) {
        super(builder);
        this._marketplace = builder.marketplace;
    }
    /**s
     * Get all attributes from localization context.
     * @returns attributes.
     */
    asAttributesMap() {
        const result = super.asAttributesMap();
        result.set(MARKETPLACE_ATTRIBUTE, this._marketplace);
        return result;
    }
    toString() {
        let stringForm = 'RetailLocalizationContext[';
        stringForm += `${LOCALE_ATTRIBUTE}=${this.getLocale()},`;
        stringForm += `${TIME_ZONE_ATTRIBUTE}=${this.getTimeZone()},`;
        stringForm += `${DEFAULT_LOCALE_ATTRIBUTE}=${this.getDefaultLocale()},`;
        stringForm += `${I18N_DOMAIN_ATTRIBUTE}=${this.getI18nDomain()},`;
        stringForm += `${MARKETPLACE_ATTRIBUTE}=${this._marketplace}]`;
        return stringForm;
    }
}
const MARKETPLACE_ATTRIBUTE = 'marketplace';

class Marketplace {
    /**
     * Method for the platform to override to have the obfuscated marketplace id retrieval of the marketplace
     * based on the platform, and return obfuscated marketplace id of the marketplace
     * @return {undefined | string} undefined
     */
    getObfuscatedMarketplaceId() {
        return undefined;
    }
    /**
     * Method for the platform to override to have the default locale retrieval of the marketplace based on
     * the platform, and return default locale of the marketplace
     *
     * @return {undefined | string} undefined
     */
    getDefaultLocale() {
        return Intl.DateTimeFormat().resolvedOptions().locale;
    }
    /**
     * Method for the platform to override to have the default currency retrieval of the marketplace based on
     * the platform, and return default currency of the marketplace
     *
     * @return {undefined | string} undefined
     */
    getDefaultCurrency() {
        return undefined;
    }
}

/**
 * EmptyMarketplace with undefined marketplaceId, locale, and currencyCode
 */
class EmptyMarketplace extends Marketplace {
}
/**
 * This class providing a provider generate EmptyMarketplace,
 * as default marketplace provider in marketplace-creator
 */
// tslint:disable-next-line:max-classes-per-file
class EmptyMarketplaceProvider {
    getMarketplace() {
        return EmptyMarketplaceProvider.emptyMarketplace;
    }
}
EmptyMarketplaceProvider.emptyMarketplace = new EmptyMarketplace();

class MarketplaceCreator {
    /**
     * Set the customMarketplaceProvider as the singleton marketplaceProvider.
     * @param {MarketplaceProvider} customMarketplaceProvider - User specified marketplace marketplaceProvider
     */
    static setProvider(customMarketplaceProvider) {
        MarketplaceCreator.marketplaceProvider = customMarketplaceProvider;
    }
    /**
     * Create singleton Marketplace marketplaceProvider which creates marketplace configuration.
     * Return the singleton marketplaceProvider if it exists.
     * Otherwise, set an EmptyMarketplaceProvider object as singleton marketplaceProvider and return it.
     * @return {MarketplaceProvider} the singleton MarketplaceProvider marketplaceProvider
     */
    static provider() {
        if (!MarketplaceCreator.marketplaceProvider) {
            MarketplaceCreator.marketplaceProvider = new EmptyMarketplaceProvider();
        }
        return MarketplaceCreator.marketplaceProvider;
    }
}
MarketplaceCreator.marketplaceProvider = undefined;

const MARKETPLACE_DEFAULTS = {
    // NA marketplaces
    A2Q3Y263D00KWC: {
        name: 'BR',
        locale: 'pt-BR',
        currency: 'BRL'
    },
    AZXD3QD5B39HD: {
        name: 'BR_DEVO',
        locale: 'pt-BR',
        currency: 'BRL'
    },
    A2EUQ1WTGCTBG2: {
        name: 'CA',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A2WDZJZ5TCRMLH: {
        name: 'CL',
        locale: 'es-CL',
        currency: 'CLP'
    },
    A2IDBVIE7EAGN4: {
        name: 'CL_DEVO',
        locale: 'es-CL',
        currency: 'CLP'
    },
    A1ZXRLSQW2V54D: {
        name: 'CO',
        locale: 'es-CO',
        currency: 'COP'
    },
    A1NOHO22DE3OHQ: {
        name: 'CO_DEVO',
        locale: 'es-CO',
        currency: 'COP'
    },
    A1AM78C64UM0Y8: {
        name: 'MX',
        locale: 'es-MX',
        currency: 'MXN'
    },
    A3P3J5A7D2ZVXI: {
        name: 'MX_DEVO',
        locale: 'es-MX',
        currency: 'MXN'
    },
    ATVPDKIKX0DER: {
        name: 'US',
        locale: 'en-US',
        currency: 'USD'
    },
    AGWSWK15IEJJ7: {
        name: 'PYOP_US_PROD',
        locale: 'en-US',
        currency: 'USD'
    },
    A3BXB0YN3XH17H: {
        name: 'PYOP_US_PROD_SANDBOX',
        locale: 'en-US',
        currency: 'USD'
    },
    A35A4JO734ER04: {
        name: 'PYOP_US_DEVO',
        locale: 'en-US',
        currency: 'USD'
    },
    A341L3VCFKNMIY: {
        name: 'PYOP_US_DEVO_SANDBOX',
        locale: 'en-US',
        currency: 'USD'
    },
    A2CE4MDCUAASDP: {
        name: 'PYOP_CA_PROD',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A2VJI655WT2I0B: {
        name: 'PYOP_CA_PROD_SANDBOX',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A1NWP2E4EKQP94: {
        name: 'PYOP_CA_DEVO',
        locale: 'en-CA',
        currency: 'CAD'
    },
    ASBG27DKM7EX6: {
        name: 'PYOP_CA_DEVO_SANDBOX',
        locale: 'en-CA',
        currency: 'CAD'
    },
    // EU marketplaces
    A2VIGQ35RCS4UG: {
        name: 'AE',
        locale: 'en-AE',
        currency: 'AED'
    },
    A34GYYCZVDBSIK: {
        name: 'AE_DEVO',
        locale: 'en-AE',
        currency: 'AED'
    },
    A1PA6795UKMFR9: {
        name: 'DE',
        locale: 'de-DE',
        currency: 'EUR'
    },
    AMEN7PMS3EDWL: {
        name: 'BE',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    A1YFOMBPFBRY22: {
        name: 'BE_DEVO',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    ARBP9OOSHTCHU: {
        name: 'EG',
        locale: 'en-AE',
        currency: 'EGP'
    },
    AUJPM9XGFJRC7: {
        name: 'EG_DEVO',
        locale: 'en-AE',
        currency: 'EGP'
    },
    A1RKKUPIHCS9HS: {
        name: 'ES',
        locale: 'es-ES',
        currency: 'EUR'
    },
    AJZF8LZ1EJVJN: {
        name: 'ES_DEVO',
        locale: 'es-ES',
        currency: 'EUR'
    },
    A13V1IB3VIYZZH: {
        name: 'FR',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    A1F83G8C2ARO7P: {
        name: 'GB',
        locale: 'en-GB',
        currency: 'GBP'
    },
    APJ6JRA9NG5V4: {
        name: 'IT',
        locale: 'it-IT',
        currency: 'EUR'
    },
    A3HOBANJMCMD83: {
        name: 'IT_DEVO',
        locale: 'it-IT',
        currency: 'EUR'
    },
    A21TJRUUN4KGV: {
        name: 'IN',
        locale: 'en-IN',
        currency: 'INR'
    },
    A2XZLSVIQ0F4JT: {
        name: 'IN_DEVO',
        locale: 'en-IN',
        currency: 'INR'
    },
    A3OCL2LJ6GDH9T: {
        name: 'NG',
        locale: 'en-NG',
        currency: 'NGN'
    },
    A3PJZM0QIA8QDU: {
        name: 'NG_DEVO',
        locale: 'en-NG',
        currency: 'NGN'
    },
    A1805IZSGTT6HS: {
        name: 'NL',
        locale: 'nl-NL',
        currency: 'EUR'
    },
    A1M3WC0SJ3A38T: {
        name: 'NL_DEVO',
        locale: 'nl-NL',
        currency: 'EUR'
    },
    A1C3SOZRARQ6R3: {
        name: 'PL',
        locale: 'pl-PL',
        currency: 'PLN'
    },
    AKY4K4WKH21YQ: {
        name: 'PL_DEVO',
        locale: 'pl-PL',
        currency: 'PLN'
    },
    A17E79C6D8DWNP: {
        name: 'SA',
        locale: 'en-AE',
        currency: 'SAR'
    },
    A1MQPSGJ6U9Q54: {
        name: 'SA_DEVO',
        locale: 'en-AE',
        currency: 'SAR'
    },
    A2NODRKZP88ZB9: {
        name: 'SE',
        locale: 'sv-SE',
        currency: 'SEK'
    },
    AT614YYYNOC1S: {
        name: 'SE_DEVO',
        locale: 'sv-SE',
        currency: 'SEK'
    },
    A33AVAJ2PDY3EV: {
        name: 'TR',
        locale: 'tr-TR',
        currency: 'TRY'
    },
    A3CQBQD3RGPJR8: {
        name: 'TR_DEVO',
        locale: 'tr-TR',
        currency: 'TRY'
    },
    AE08WJ6YKNBMC: {
        name: 'ZA',
        locale: 'en-ZA',
        currency: 'ZAR'
    },
    A3TFQ1G9L8TQ5L: {
        name: 'ZA_DEVO',
        locale: 'en-ZA',
        currency: 'ZAR'
    },
    AZAJMM36N6WQL: {
        name: 'PYOP_UK_PROD',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A3M3RRFO9XDT2G: {
        name: 'PYOP_UK_PROD_SANDBOX',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A2UWQKHEFGNQY3: {
        name: 'PYOP_UK_DEVO',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A2V84NPDZD3ZEH: {
        name: 'PYOP_UK_DEVO_SANDBOX',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A53RDEWN57UU5: {
        name: 'PYOP_DE_PROD',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A1G8446IYHA4MR: {
        name: 'PYOP_DE_PROD_SANDBOX',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A34RX6LWLXD2KA: {
        name: 'PYOP_DE_DEVO',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A225AO9WA6YYOF: {
        name: 'PYOP_DE_DEVO_SANDBOX',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A3FDG49KKM823Y: {
        name: 'PYOP_IN_PROD',
        locale: 'en-IN',
        currency: 'INR'
    },
    A1IAUC0NN979H6: {
        name: 'PYOP_IN_PROD_SANDBOX',
        locale: 'en-IN',
        currency: 'INR'
    },
    A18VPDB9UTK24D: {
        name: 'PYOP_IN_DEVO',
        locale: 'en-IN',
        currency: 'INR'
    },
    A16Z2XWZJTFS70: {
        name: 'PYOP_IN_DEVO_SANDBOX',
        locale: 'en-IN',
        currency: 'INR'
    },
    // FE marketplaces
    A39IBJ37TRP1C6: {
        name: 'AU',
        locale: 'en-AU',
        currency: 'AUD'
    },
    A1RNPCQ4K8U27I: {
        name: 'AU_DEVO',
        locale: 'en-AU',
        currency: 'AUD'
    },
    A1VC38T7YXB528: {
        name: 'JP',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    AD2EMQ3L3PG8S: {
        name: 'RU',
        locale: 'ru-RU',
        currency: 'RUB'
    },
    A38NPJYVS5YHNH: {
        name: 'RU_DEVO',
        locale: 'ru-RU',
        currency: 'RUB'
    },
    A19VAU5U5O7RUS: {
        name: 'SG',
        locale: 'en-SG',
        currency: 'SGD'
    },
    A1DQ6JV7I20JSG: {
        name: 'SG_DEVO',
        locale: 'en-SG',
        currency: 'SGD'
    },
    A75P1MG8VMU51: {
        name: 'PYOP_JP_PROD',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A31YDYE76E6TCP: {
        name: 'PYOP_JP_PROD_SANDBOX',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A2EFPHRCZ60KI3: {
        name: 'PYOP_JP_DEVO',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A1U1OSY46WLA42: {
        name: 'PYOP_JP_DEVO_SANDBOX',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    // CN Marketplaces
    AAHKV2X7AFYLW: {
        name: 'CN',
        locale: 'zh-CN',
        currency: 'CNY'
    }
};

/**
 * This class is based on the Java DefaultMarketplaceImpl,
 * providing default marketplace specific implementation.
 */
class DefaultMarketplaceImpl extends Marketplace {
    /**
     * This constructor enables consumers to override the default locale and default currency for the valid amazon
     * marketplace IDs.
     * If the default locale or default currency is passed as undefined for the valid obfuscatedMarketplaceId as key,
     * then the default locale or default currency will be retrieved from the MARKETPLACE_DEFAULTS mapping.
     * If it is a invalid amazon retail marketplace ID, and will have, the associated values will be assigned as undefined
     *
     * @param {undefined | string} obfuscatedMarketplaceIdParam - obfuscated marketplace id of the marketplace
     * @param {undefined | string} defaultLocaleParam - default locale of the marketplace
     * @param {undefined | string} defaultCurrencyParam - default currency of the marketplace
     */
    constructor(obfuscatedMarketplaceId, defaultLocale, defaultCurrency) {
        super();
        // the obfuscatedMarketplaceId must be presented to look up the default locale and currency
        if (obfuscatedMarketplaceId) {
            this.obfuscatedMarketplaceId = obfuscatedMarketplaceId;
            const defaultValues = MARKETPLACE_DEFAULTS[obfuscatedMarketplaceId];
            this.defaultLocale = defaultLocale
                ? defaultLocale
                : defaultValues
                    ? defaultValues.locale
                    : super.getDefaultLocale();
            this.defaultCurrency = defaultCurrency
                ? defaultCurrency
                : defaultValues
                    ? defaultValues.currency
                    : undefined;
        }
        else {
            this.obfuscatedMarketplaceId = undefined;
            this.defaultLocale = undefined;
            this.defaultCurrency = undefined;
        }
    }
    /**
     * get the default obfuscatedMarketplaceId of the marketplace with default implementation behavior
     * @return {undefined | string} obfuscatedMarketplaceId - default obfuscatedMarketplaceId of the marketplace
     */
    getObfuscatedMarketplaceId() {
        return this.obfuscatedMarketplaceId;
    }
    /**
     * get the default locale of the marketplace with default implementation behavior
     * @return {undefined | string} defaultLocale - default locale of the marketplace
     */
    getDefaultLocale() {
        return this.defaultLocale;
    }
    /**
     * get the default currency of the marketplace with default implementation behavior
     * @return {undefined | string} defaultCurrency - default currency of the marketplace
     */
    getDefaultCurrency() {
        return this.defaultCurrency;
    }
    toString() {
        return `Marketplace[obfuscatedMarketplaceId=${this.getObfuscatedMarketplaceId()},defaultLocale=${this.getDefaultLocale()},defaultCurrency=${this.getDefaultCurrency()}]`;
    }
    /**
     * Returns a map of all Amazon Marketplaces and their corresponding ObfuscatedIds.
     * e.g. GB = A1F83G8C2ARO7P
     *      AE_DEVO = A34GYYCZVDBSIK
     *
     * @return {Map<string, string>} map of marketplaces and their obfuscated ids.
     */
    static getObfuscatedMarketplaceIdMap() {
        const map = new Map();
        for (const marketplaceId of Object.keys(MARKETPLACE_DEFAULTS)) {
            map.set(MARKETPLACE_DEFAULTS[marketplaceId].name, marketplaceId);
        }
        return map;
    }
}

/**
 * Builder class for {@link RetailLocalizationContext}.
 */
class RetailLocalizationContextBuilder extends LocalizationContextBuilder {
    constructor() {
        super();
        this._marketplace = MarketplaceCreator.provider().getMarketplace();
    }
    /**
     * Gets marketplace.
     */
    get marketplace() {
        return this._marketplace;
    }
    /**
     * Sets marketplace on context corresponding to the given obfuscated marketplace id.
     * @param obfuscatedMarketplaceId
     * @returns a reference to this object with marketplace set
     */
    withMarketplace(obfuscatedMarketplaceId) {
        this._marketplace = new DefaultMarketplaceImpl(obfuscatedMarketplaceId);
        return this;
    }
    /**
     * Builds retail localization context.
     * @returns RetailLocalizationContext
     */
    build() {
        return new RetailLocalizationContext(this);
    }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

class QuickLRU {
	constructor(options = {}) {
		if (!(options.maxSize && options.maxSize > 0)) {
			throw new TypeError('`maxSize` must be a number greater than 0');
		}

		this.maxSize = options.maxSize;
		this.onEviction = options.onEviction;
		this.cache = new Map();
		this.oldCache = new Map();
		this._size = 0;
	}

	_set(key, value) {
		this.cache.set(key, value);
		this._size++;

		if (this._size >= this.maxSize) {
			this._size = 0;

			if (typeof this.onEviction === 'function') {
				for (const [key, value] of this.oldCache.entries()) {
					this.onEviction(key, value);
				}
			}

			this.oldCache = this.cache;
			this.cache = new Map();
		}
	}

	get(key) {
		if (this.cache.has(key)) {
			return this.cache.get(key);
		}

		if (this.oldCache.has(key)) {
			const value = this.oldCache.get(key);
			this.oldCache.delete(key);
			this._set(key, value);
			return value;
		}
	}

	set(key, value) {
		if (this.cache.has(key)) {
			this.cache.set(key, value);
		} else {
			this._set(key, value);
		}

		return this;
	}

	has(key) {
		return this.cache.has(key) || this.oldCache.has(key);
	}

	peek(key) {
		if (this.cache.has(key)) {
			return this.cache.get(key);
		}

		if (this.oldCache.has(key)) {
			return this.oldCache.get(key);
		}
	}

	delete(key) {
		const deleted = this.cache.delete(key);
		if (deleted) {
			this._size--;
		}

		return this.oldCache.delete(key) || deleted;
	}

	clear() {
		this.cache.clear();
		this.oldCache.clear();
		this._size = 0;
	}

	* keys() {
		for (const [key] of this) {
			yield key;
		}
	}

	* values() {
		for (const [, value] of this) {
			yield value;
		}
	}

	* [Symbol.iterator]() {
		for (const item of this.cache) {
			yield item;
		}

		for (const item of this.oldCache) {
			const [key] = item;
			if (!this.cache.has(key)) {
				yield item;
			}
		}
	}

	get size() {
		let oldCacheSize = 0;
		for (const key of this.oldCache.keys()) {
			if (!this.cache.has(key)) {
				oldCacheSize++;
			}
		}

		return Math.min(this._size + oldCacheSize, this.maxSize);
	}
}

var quickLru = QuickLRU;

var QuickLRU$1 = quickLru;

/**
 * Default serialize key function.
 * @param key
 * @returns serialized key
 */
const defaultSerializeKeyFn = (key) => String(key);
/**
 * [Loading cache](https://stackoverflow.com/questions/43993731/what-is-a-loadingcache) backed by QuickLRU to
 * evict the resources. CacheKey function, can be provided for supporting injection of the Object types with
 * value equality.
 * const obj1 = {key: 'value'} is same as const obj2 = {key: 'value'} in cache (different references).
 *
 * Reference equality for the objects would not work in this case and we do not use it.
 */
class LoadingCache {
    /**
     * Construct loading cache.
     *
     * @param loader Loader function for getting the cache values.
     * @param cacheOptions QuickLRU plus cache key options.
     */
    constructor(loader, cacheOptions) {
        this.loader = loader;
        this.storage = new QuickLRU$1(cacheOptions);
        this.serializeKeyFn = cacheOptions.keyFn !== undefined ? cacheOptions.keyFn : defaultSerializeKeyFn;
    }
    /**
     * Retrieve the existing value from the cache or use loader to compute and cache it.
     * @param key Key to lookup.
     * @returns value.
     */
    get(key) {
        const cacheKey = this.serializeKeyFn(key);
        const cached = this.storage.get(cacheKey);
        if (cached) {
            return cached;
        }
        const content = this.loader(key);
        if (content === undefined) {
            return undefined;
        }
        this.storage.set(cacheKey, content);
        return content;
    }
    /**
     * Size of loading cache.
     * @returns size
     */
    size() {
        return this.storage.size;
    }
    /**
     * Check if key exists.
     * @param key
     * @returns true if has
     */
    has(key) {
        return this.storage.has(this.serializeKeyFn(key));
    }
}

/**
 * Message bundle cache key based upon Panther resource bundle and localization context.
 */
class MessageBundleCacheKey {
    constructor(_bundle, _context) {
        this._bundle = _bundle;
        this._context = _context;
    }
    /**
     * Gets bundle from MessageBundleCacheKey.
     */
    get bundle() {
        return this._bundle;
    }
    /**
     * Gets context from MessageBundleCacheKey.
     */
    get context() {
        return this._context;
    }
    /**
     * Creating cache key from Panther resource bundle and context.
     * @param messageBundleKey
     * @returns cache key
     */
    static generateCacheKey(messageBundleKey) {
        let bundleKey = messageBundleKey.bundle;
        const attributes = messageBundleKey.context.asAttributesMap();
        for (const key of MessageBundleCacheKey.LOCALIZATION_CONTEXT_ATTRIBUTES_FOR_CACHE_KEY) {
            const value = attributes.get(key);
            if (value) {
                bundleKey += `#${key}#${value}`;
            }
        }
        return bundleKey;
    }
}
/**
 * LocalizationContext attributes for cache key construction
 */
MessageBundleCacheKey.LOCALIZATION_CONTEXT_ATTRIBUTES_FOR_CACHE_KEY = [
    LOCALE_ATTRIBUTE,
    DEFAULT_LOCALE_ATTRIBUTE,
    I18N_DOMAIN_ATTRIBUTE,
];

const CONSTANTS = {
    rootLocale: 'und',
    noManifestError: 'Manifest can not be null!',
    invalidBundleError: 'Invalid bundle is passed!',
};

/**
 * Resource fallback item representing the resource to load. Bundle, variants and locale map to the
 * the resource file in ARB.
 */
class FallbackItem {
    /**
     * Creates an instance of fallback item.
     * @param bundle Panther resource bundle.
     * @param locale Locale of the resource.
     * @param [variants] Variants like i18nDomain.
     */
    constructor(bundle, locale, variants) {
        this._bundle = bundle;
        this._variants = variants;
        this._locale = locale;
    }
    /**
     * Gets Panther resource bundle
     */
    get bundle() {
        return this._bundle;
    }
    /**
     * Gets variants
     */
    get variants() {
        return this._variants;
    }
    /**
     * Gets locale
     */
    get locale() {
        return this._locale;
    }
    /**
     * Cache key for the fallback item to be used while inserting into the loading cache.
     * @param item Fallback item.
     * @returns cache key.
     */
    static cacheKey(item) {
        let result = item.bundle;
        if (item.variants) {
            item.variants.forEach((value, key) => {
                result += `#var-${key}#${value}`;
            });
        }
        result += `#${item.locale}`;
        return result;
    }
}
/**
 * Locale and variant fallback generator for provided localization context using iterator.
 */
class ResourceFallbackHandler {
    /**
     * Sparse fallback chains are created using iterators in the following format:
     *
     * @param bundle : Panther resource bundle.
     * @param variantsFallbackChain : Fallback chain coming from manifest.
     * @param localizationContext : Localization context.
     * @returns fallback chains : Sparse fallback chains.
     */
    getFallbackChains(bundle, variantsFallbackChain, localizationContext) {
        const localeChain = this.createLocaleFallbackChain(localizationContext.getLocale(), localizationContext.getDefaultLocale());
        const variantChainIterator = this.getVariantFallbackChain(variantsFallbackChain, localizationContext);
        return {
            *[Symbol.iterator]() {
                // Using generator for lazy looping.
                for (const currentVariantChain of variantChainIterator) {
                    for (const variants of currentVariantChain) {
                        for (const locale of localeChain) {
                            yield new FallbackItem(bundle, locale, variants);
                        }
                    }
                }
                // plain locale chain at the end.
                for (const locale of localeChain) {
                    yield new FallbackItem(bundle, locale, undefined);
                }
            },
        };
    }
    /**
     * Form variant fallback chains.
     * @param fallbackDefinitions  : Fallback chain coming from manifest.
     * @param localizationContext : Localization context.
     * @returns variant fallback chain
     */
    getVariantFallbackChain(fallbackDefinitions, localizationContext) {
        const contextAttributes = localizationContext.asAttributesMap();
        return {
            *[Symbol.iterator]() {
                for (let index = 0; index < fallbackDefinitions.length; index++) {
                    let nextChain = [];
                    if (index < fallbackDefinitions.length - 1) {
                        nextChain = fallbackDefinitions[index + 1];
                    }
                    const currentChain = fallbackDefinitions[index];
                    // To avoid creating duplicate chains if the prefix of the next fallback chain matches.
                    let nextChainPrefix = true;
                    const variants = [];
                    const currentVariant = new Map();
                    for (let currentChainIndex = 0; currentChainIndex < currentChain.length; currentChainIndex++) {
                        if (!contextAttributes) {
                            continue;
                        }
                        const variant = currentChain[currentChainIndex];
                        const variantValue = contextAttributes.get(variant);
                        if (!variantValue) {
                            continue;
                        }
                        currentVariant.set(variant, variantValue);
                        if (nextChainPrefix &&
                            currentChainIndex < nextChain.length &&
                            nextChain[currentChainIndex] === currentChain[currentChainIndex]) {
                            continue;
                        }
                        nextChainPrefix = false;
                        variants.push(new Map(currentVariant));
                    }
                    yield variants.reverse();
                }
            },
        };
    }
    /**
     * Creates locale fallback chain.
     * @param locale
     * @param [defaultLocale]
     * @returns locale fallback chain
     */
    createLocaleFallbackChain(locale, defaultLocale) {
        // @ts-ignore
        const canonicalLocale = Intl.getCanonicalLocales(locale)[0];
        const canonicalDefaultLocale = 
        // @ts-ignore
        defaultLocale !== undefined ? Intl.getCanonicalLocales(defaultLocale)[0] : defaultLocale;
        const localeChain = this.getFallbackChain(canonicalLocale);
        // add default locale.
        if (canonicalDefaultLocale && !localeChain.includes(canonicalDefaultLocale)) {
            const defaultLocaleChain = this.getFallbackChain(canonicalDefaultLocale);
            localeChain.push(...defaultLocaleChain);
        }
        localeChain.push(CONSTANTS.rootLocale);
        return localeChain;
    }
    /**
     * Gets fallback chain for provided locale.
     * @param locale
     * @returns fallback chain
     */
    getFallbackChain(locale) {
        const localeChain = [];
        // @ts-ignore
        const intlLocale = new Intl.Locale(locale);
        let fallbackLocale = intlLocale.baseName;
        if (fallbackLocale !== CONSTANTS.rootLocale) {
            localeChain.push(fallbackLocale);
            let toTrim = fallbackLocale.lastIndexOf(ResourceFallbackHandler.LOCALE_TAG_SEPARATOR);
            while (toTrim > 0) {
                fallbackLocale = fallbackLocale.substr(0, toTrim);
                localeChain.push(fallbackLocale);
                toTrim = fallbackLocale.lastIndexOf(ResourceFallbackHandler.LOCALE_TAG_SEPARATOR);
            }
        }
        return localeChain;
    }
}
ResourceFallbackHandler.LOCALE_TAG_SEPARATOR = '-';

var ErrorKind;
(function (ErrorKind) {
    /** Argument is unclosed (e.g. `{0`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_CLOSING_BRACE"] = 1] = "EXPECT_ARGUMENT_CLOSING_BRACE";
    /** Argument is empty (e.g. `{}`). */
    ErrorKind[ErrorKind["EMPTY_ARGUMENT"] = 2] = "EMPTY_ARGUMENT";
    /** Argument is malformed (e.g. `{foo!}``) */
    ErrorKind[ErrorKind["MALFORMED_ARGUMENT"] = 3] = "MALFORMED_ARGUMENT";
    /** Expect an argument type (e.g. `{foo,}`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_TYPE"] = 4] = "EXPECT_ARGUMENT_TYPE";
    /** Unsupported argument type (e.g. `{foo,foo}`) */
    ErrorKind[ErrorKind["INVALID_ARGUMENT_TYPE"] = 5] = "INVALID_ARGUMENT_TYPE";
    /** Expect an argument style (e.g. `{foo, number, }`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_STYLE"] = 6] = "EXPECT_ARGUMENT_STYLE";
    /** The number skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_NUMBER_SKELETON"] = 7] = "INVALID_NUMBER_SKELETON";
    /** The date time skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_DATE_TIME_SKELETON"] = 8] = "INVALID_DATE_TIME_SKELETON";
    /** Exepct a number skeleton following the `::` (e.g. `{foo, number, ::}`) */
    ErrorKind[ErrorKind["EXPECT_NUMBER_SKELETON"] = 9] = "EXPECT_NUMBER_SKELETON";
    /** Exepct a date time skeleton following the `::` (e.g. `{foo, date, ::}`) */
    ErrorKind[ErrorKind["EXPECT_DATE_TIME_SKELETON"] = 10] = "EXPECT_DATE_TIME_SKELETON";
    /** Unmatched apostrophes in the argument style (e.g. `{foo, number, 'test`) */
    ErrorKind[ErrorKind["UNCLOSED_QUOTE_IN_ARGUMENT_STYLE"] = 11] = "UNCLOSED_QUOTE_IN_ARGUMENT_STYLE";
    /** Missing select argument options (e.g. `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_OPTIONS"] = 12] = "EXPECT_SELECT_ARGUMENT_OPTIONS";
    /** Expecting an offset value in `plural` or `selectordinal` argument (e.g `{foo, plural, offset}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE"] = 13] = "EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Offset value in `plural` or `selectordinal` is invalid (e.g. `{foo, plural, offset: x}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_OFFSET_VALUE"] = 14] = "INVALID_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Expecting a selector in `select` argument (e.g `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR"] = 15] = "EXPECT_SELECT_ARGUMENT_SELECTOR";
    /** Expecting a selector in `plural` or `selectordinal` argument (e.g `{foo, plural}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR"] = 16] = "EXPECT_PLURAL_ARGUMENT_SELECTOR";
    /** Expecting a message fragment after the `select` selector (e.g. `{foo, select, apple}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT"] = 17] = "EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT";
    /**
     * Expecting a message fragment after the `plural` or `selectordinal` selector
     * (e.g. `{foo, plural, one}`)
     */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT"] = 18] = "EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT";
    /** Selector in `plural` or `selectordinal` is malformed (e.g. `{foo, plural, =x {#}}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_SELECTOR"] = 19] = "INVALID_PLURAL_ARGUMENT_SELECTOR";
    /**
     * Duplicate selectors in `plural` or `selectordinal` argument.
     * (e.g. {foo, plural, one {#} one {#}})
     */
    ErrorKind[ErrorKind["DUPLICATE_PLURAL_ARGUMENT_SELECTOR"] = 20] = "DUPLICATE_PLURAL_ARGUMENT_SELECTOR";
    /** Duplicate selectors in `select` argument.
     * (e.g. {foo, select, apple {apple} apple {apple}})
     */
    ErrorKind[ErrorKind["DUPLICATE_SELECT_ARGUMENT_SELECTOR"] = 21] = "DUPLICATE_SELECT_ARGUMENT_SELECTOR";
    /** Plural or select argument option must have `other` clause. */
    ErrorKind[ErrorKind["MISSING_OTHER_CLAUSE"] = 22] = "MISSING_OTHER_CLAUSE";
    /** The tag is malformed. (e.g. `<bold!>foo</bold!>) */
    ErrorKind[ErrorKind["INVALID_TAG"] = 23] = "INVALID_TAG";
    /** The tag name is invalid. (e.g. `<123>foo</123>`) */
    ErrorKind[ErrorKind["INVALID_TAG_NAME"] = 25] = "INVALID_TAG_NAME";
    /** The closing tag does not match the opening tag. (e.g. `<bold>foo</italic>`) */
    ErrorKind[ErrorKind["UNMATCHED_CLOSING_TAG"] = 26] = "UNMATCHED_CLOSING_TAG";
    /** The opening tag has unmatched closing tag. (e.g. `<bold>foo`) */
    ErrorKind[ErrorKind["UNCLOSED_TAG"] = 27] = "UNCLOSED_TAG";
})(ErrorKind || (ErrorKind = {}));

var TYPE;
(function (TYPE) {
    /**
     * Raw text
     */
    TYPE[TYPE["literal"] = 0] = "literal";
    /**
     * Variable w/o any format, e.g `var` in `this is a {var}`
     */
    TYPE[TYPE["argument"] = 1] = "argument";
    /**
     * Variable w/ number format
     */
    TYPE[TYPE["number"] = 2] = "number";
    /**
     * Variable w/ date format
     */
    TYPE[TYPE["date"] = 3] = "date";
    /**
     * Variable w/ time format
     */
    TYPE[TYPE["time"] = 4] = "time";
    /**
     * Variable w/ select format
     */
    TYPE[TYPE["select"] = 5] = "select";
    /**
     * Variable w/ plural format
     */
    TYPE[TYPE["plural"] = 6] = "plural";
    /**
     * Only possible within plural argument.
     * This is the `#` symbol that will be substituted with the count.
     */
    TYPE[TYPE["pound"] = 7] = "pound";
    /**
     * XML-like tag
     */
    TYPE[TYPE["tag"] = 8] = "tag";
    TYPE[TYPE["price"] = 9] = "price";
})(TYPE || (TYPE = {}));
var SKELETON_TYPE;
(function (SKELETON_TYPE) {
    SKELETON_TYPE[SKELETON_TYPE["number"] = 0] = "number";
    SKELETON_TYPE[SKELETON_TYPE["dateTime"] = 1] = "dateTime";
})(SKELETON_TYPE || (SKELETON_TYPE = {}));
/**
 * Type Guards
 */
function isLiteralElement(el) {
    return el.type === TYPE.literal;
}
function isArgumentElement(el) {
    return el.type === TYPE.argument;
}
function isNumberElement(el) {
    return el.type === TYPE.number;
}
function isPriceElement(el) {
    return el.type === TYPE.price;
}
function isDateElement(el) {
    return el.type === TYPE.date;
}
function isTimeElement(el) {
    return el.type === TYPE.time;
}
function isSelectElement(el) {
    return el.type === TYPE.select;
}
function isPluralElement(el) {
    return el.type === TYPE.plural;
}
function isPoundElement(el) {
    return el.type === TYPE.pound;
}
function isTagElement(el) {
    return el.type === TYPE.tag;
}
function isNumberSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.number);
}
function isDateTimeSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.dateTime);
}

// @generated from regex-gen.ts
const SPACE_SEPARATOR_START_REGEX = /^[ \xA0\u1680\u2000-\u200A\u202F\u205F\u3000]*/i;
const SPACE_SEPARATOR_END_REGEX = /[ \xA0\u1680\u2000-\u200A\u202F\u205F\u3000]*$/i;

/**
 * https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * Credit: https://github.com/caridy/intl-datetimeformat-pattern/blob/master/index.js
 * with some tweaks
 */
var DATE_TIME_REGEX = /(?:[Eec]{1,6}|G{1,5}|[Qq]{1,5}|(?:[yYur]+|U{1,5})|[ML]{1,5}|d{1,2}|D{1,3}|F{1}|[abB]{1,5}|[hkHK]{1,2}|w{1,2}|W{1}|m{1,2}|s{1,2}|[zZOvVxX]{1,4})(?=([^']*'[^']*')*[^']*$)/g;
/**
 * Parse Date time skeleton into Intl.DateTimeFormatOptions
 * Ref: https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * @public
 * @param skeleton skeleton string
 */
function parseDateTimeSkeleton(skeleton) {
    var result = {};
    skeleton.replace(DATE_TIME_REGEX, function (match) {
        var len = match.length;
        switch (match[0]) {
            // Era
            case 'G':
                result.era = len === 4 ? 'long' : len === 5 ? 'narrow' : 'short';
                break;
            // Year
            case 'y':
                result.year = len === 2 ? '2-digit' : 'numeric';
                break;
            case 'Y':
            case 'u':
            case 'U':
            case 'r':
                throw new RangeError('`Y/u/U/r` (year) patterns are not supported, use `y` instead');
            // Quarter
            case 'q':
            case 'Q':
                throw new RangeError('`q/Q` (quarter) patterns are not supported');
            // Month
            case 'M':
            case 'L':
                result.month = ['numeric', '2-digit', 'short', 'long', 'narrow'][len - 1];
                break;
            // Week
            case 'w':
            case 'W':
                throw new RangeError('`w/W` (week) patterns are not supported');
            case 'd':
                result.day = ['numeric', '2-digit'][len - 1];
                break;
            case 'D':
            case 'F':
            case 'g':
                throw new RangeError('`D/F/g` (day) patterns are not supported, use `d` instead');
            // Weekday
            case 'E':
                result.weekday = len === 4 ? 'short' : len === 5 ? 'narrow' : 'short';
                break;
            case 'e':
                if (len < 4) {
                    throw new RangeError('`e..eee` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            case 'c':
                if (len < 4) {
                    throw new RangeError('`c..ccc` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            // Period
            case 'a': // AM, PM
                result.hour12 = true;
                break;
            case 'b': // am, pm, noon, midnight
            case 'B': // flexible day periods
                throw new RangeError('`b/B` (period) patterns are not supported, use `a` instead');
            // Hour
            case 'h':
                result.hourCycle = 'h12';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'H':
                result.hourCycle = 'h23';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'K':
                result.hourCycle = 'h11';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'k':
                result.hourCycle = 'h24';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'j':
            case 'J':
            case 'C':
                throw new RangeError('`j/J/C` (hour) patterns are not supported, use `h/H/K/k` instead');
            // Minute
            case 'm':
                result.minute = ['numeric', '2-digit'][len - 1];
                break;
            // Second
            case 's':
                result.second = ['numeric', '2-digit'][len - 1];
                break;
            case 'S':
            case 'A':
                throw new RangeError('`S/A` (second) patterns are not supported, use `s` instead');
            // Zone
            case 'z': // 1..3, 4: specific non-location format
                result.timeZoneName = len < 4 ? 'short' : 'long';
                break;
            case 'Z': // 1..3, 4, 5: The ISO8601 varios formats
            case 'O': // 1, 4: miliseconds in day short, long
            case 'v': // 1, 4: generic non-location format
            case 'V': // 1, 2, 3, 4: time zone ID or city
            case 'X': // 1, 2, 3, 4: The ISO8601 varios formats
            case 'x': // 1, 2, 3, 4: The ISO8601 varios formats
                throw new RangeError('`Z/O/v/V/X/x` (timeZone) patterns are not supported, use `z` instead');
        }
        return '';
    });
    return result;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

// @generated from regex-gen.ts
var WHITE_SPACE_REGEX = /[\t-\r \x85\u200E\u200F\u2028\u2029]/i;

function parseNumberSkeletonFromString(skeleton) {
    if (skeleton.length === 0) {
        throw new Error('Number skeleton cannot be empty');
    }
    // Parse the skeleton
    var stringTokens = skeleton
        .split(WHITE_SPACE_REGEX)
        .filter(function (x) { return x.length > 0; });
    var tokens = [];
    for (var _i = 0, stringTokens_1 = stringTokens; _i < stringTokens_1.length; _i++) {
        var stringToken = stringTokens_1[_i];
        var stemAndOptions = stringToken.split('/');
        if (stemAndOptions.length === 0) {
            throw new Error('Invalid number skeleton');
        }
        var stem = stemAndOptions[0], options = stemAndOptions.slice(1);
        for (var _a = 0, options_1 = options; _a < options_1.length; _a++) {
            var option = options_1[_a];
            if (option.length === 0) {
                throw new Error('Invalid number skeleton');
            }
        }
        tokens.push({ stem: stem, options: options });
    }
    return tokens;
}
function icuUnitToEcma(unit) {
    return unit.replace(/^(.*?)-/, '');
}
var FRACTION_PRECISION_REGEX = /^\.(?:(0+)(\*)?|(#+)|(0+)(#+))$/g;
var SIGNIFICANT_PRECISION_REGEX = /^(@+)?(\+|#+)?$/g;
var INTEGER_WIDTH_REGEX = /(\*)(0+)|(#+)(0+)|(0+)/g;
var CONCISE_INTEGER_WIDTH_REGEX = /^(0+)$/;
function parseSignificantPrecision(str) {
    var result = {};
    str.replace(SIGNIFICANT_PRECISION_REGEX, function (_, g1, g2) {
        // @@@ case
        if (typeof g2 !== 'string') {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits = g1.length;
        }
        // @@@+ case
        else if (g2 === '+') {
            result.minimumSignificantDigits = g1.length;
        }
        // .### case
        else if (g1[0] === '#') {
            result.maximumSignificantDigits = g1.length;
        }
        // .@@## or .@@@ case
        else {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits =
                g1.length + (typeof g2 === 'string' ? g2.length : 0);
        }
        return '';
    });
    return result;
}
function parseSign(str) {
    switch (str) {
        case 'sign-auto':
            return {
                signDisplay: 'auto',
            };
        case 'sign-accounting':
        case '()':
            return {
                currencySign: 'accounting',
            };
        case 'sign-always':
        case '+!':
            return {
                signDisplay: 'always',
            };
        case 'sign-accounting-always':
        case '()!':
            return {
                signDisplay: 'always',
                currencySign: 'accounting',
            };
        case 'sign-except-zero':
        case '+?':
            return {
                signDisplay: 'exceptZero',
            };
        case 'sign-accounting-except-zero':
        case '()?':
            return {
                signDisplay: 'exceptZero',
                currencySign: 'accounting',
            };
        case 'sign-never':
        case '+_':
            return {
                signDisplay: 'never',
            };
    }
}
function parseConciseScientificAndEngineeringStem(stem) {
    // Engineering
    var result;
    if (stem[0] === 'E' && stem[1] === 'E') {
        result = {
            notation: 'engineering',
        };
        stem = stem.slice(2);
    }
    else if (stem[0] === 'E') {
        result = {
            notation: 'scientific',
        };
        stem = stem.slice(1);
    }
    if (result) {
        var signDisplay = stem.slice(0, 2);
        if (signDisplay === '+!') {
            result.signDisplay = 'always';
            stem = stem.slice(2);
        }
        else if (signDisplay === '+?') {
            result.signDisplay = 'exceptZero';
            stem = stem.slice(2);
        }
        if (!CONCISE_INTEGER_WIDTH_REGEX.test(stem)) {
            throw new Error('Malformed concise eng/scientific notation');
        }
        result.minimumIntegerDigits = stem.length;
    }
    return result;
}
function parseNotationOptions(opt) {
    var result = {};
    var signOpts = parseSign(opt);
    if (signOpts) {
        return signOpts;
    }
    return result;
}
/**
 * https://github.com/unicode-org/icu/blob/master/docs/userguide/format_parse/numbers/skeletons.md#skeleton-stems-and-options
 */
function parseNumberSkeleton(tokens) {
    var result = {};
    for (var _i = 0, tokens_1 = tokens; _i < tokens_1.length; _i++) {
        var token = tokens_1[_i];
        switch (token.stem) {
            case 'percent':
            case '%':
                result.style = 'percent';
                continue;
            case '%x100':
                result.style = 'percent';
                result.scale = 100;
                continue;
            case 'currency':
                result.style = 'currency';
                result.currency = token.options[0];
                continue;
            case 'group-off':
            case ',_':
                result.useGrouping = false;
                continue;
            case 'precision-integer':
            case '.':
                result.maximumFractionDigits = 0;
                continue;
            case 'measure-unit':
            case 'unit':
                result.style = 'unit';
                result.unit = icuUnitToEcma(token.options[0]);
                continue;
            case 'compact-short':
            case 'K':
                result.notation = 'compact';
                result.compactDisplay = 'short';
                continue;
            case 'compact-long':
            case 'KK':
                result.notation = 'compact';
                result.compactDisplay = 'long';
                continue;
            case 'scientific':
                result = __assign(__assign(__assign({}, result), { notation: 'scientific' }), token.options.reduce(function (all, opt) { return (__assign(__assign({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'engineering':
                result = __assign(__assign(__assign({}, result), { notation: 'engineering' }), token.options.reduce(function (all, opt) { return (__assign(__assign({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'notation-simple':
                result.notation = 'standard';
                continue;
            // https://github.com/unicode-org/icu/blob/master/icu4c/source/i18n/unicode/unumberformatter.h
            case 'unit-width-narrow':
                result.currencyDisplay = 'narrowSymbol';
                result.unitDisplay = 'narrow';
                continue;
            case 'unit-width-short':
                result.currencyDisplay = 'code';
                result.unitDisplay = 'short';
                continue;
            case 'unit-width-full-name':
                result.currencyDisplay = 'name';
                result.unitDisplay = 'long';
                continue;
            case 'unit-width-iso-code':
                result.currencyDisplay = 'symbol';
                continue;
            case 'scale':
                result.scale = parseFloat(token.options[0]);
                continue;
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
            case 'integer-width':
                if (token.options.length > 1) {
                    throw new RangeError('integer-width stems only accept a single optional option');
                }
                token.options[0].replace(INTEGER_WIDTH_REGEX, function (_, g1, g2, g3, g4, g5) {
                    if (g1) {
                        result.minimumIntegerDigits = g2.length;
                    }
                    else if (g3 && g4) {
                        throw new Error('We currently do not support maximum integer digits');
                    }
                    else if (g5) {
                        throw new Error('We currently do not support exact integer digits');
                    }
                    return '';
                });
                continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
        if (CONCISE_INTEGER_WIDTH_REGEX.test(token.stem)) {
            result.minimumIntegerDigits = token.stem.length;
            continue;
        }
        if (FRACTION_PRECISION_REGEX.test(token.stem)) {
            // Precision
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#fraction-precision
            // precision-integer case
            if (token.options.length > 1) {
                throw new RangeError('Fraction-precision stems only accept a single optional option');
            }
            token.stem.replace(FRACTION_PRECISION_REGEX, function (_, g1, g2, g3, g4, g5) {
                // .000* case (before ICU67 it was .000+)
                if (g2 === '*') {
                    result.minimumFractionDigits = g1.length;
                }
                // .### case
                else if (g3 && g3[0] === '#') {
                    result.maximumFractionDigits = g3.length;
                }
                // .00## case
                else if (g4 && g5) {
                    result.minimumFractionDigits = g4.length;
                    result.maximumFractionDigits = g4.length + g5.length;
                }
                else {
                    result.minimumFractionDigits = g1.length;
                    result.maximumFractionDigits = g1.length;
                }
                return '';
            });
            if (token.options.length) {
                result = __assign(__assign({}, result), parseSignificantPrecision(token.options[0]));
            }
            continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#significant-digits-precision
        if (SIGNIFICANT_PRECISION_REGEX.test(token.stem)) {
            result = __assign(__assign({}, result), parseSignificantPrecision(token.stem));
            continue;
        }
        var signOpts = parseSign(token.stem);
        if (signOpts) {
            result = __assign(__assign({}, result), signOpts);
        }
        var conciseScientificAndEngineeringOpts = parseConciseScientificAndEngineeringStem(token.stem);
        if (conciseScientificAndEngineeringOpts) {
            result = __assign(__assign({}, result), conciseScientificAndEngineeringOpts);
        }
    }
    return result;
}

function createLocation(start, end) {
    return { start, end };
}
// #region Ponyfills
// Consolidate these variables up top for easier toggling during debugging
const hasNativeStartsWith = !!String.prototype.startsWith;
const hasNativeFromCodePoint = !!String.fromCodePoint;
const hasNativeFromEntries = !!Object.fromEntries;
const hasNativeCodePointAt = !!String.prototype.codePointAt;
const hasTrimStart = !!String.prototype.trimStart;
const hasTrimEnd = !!String.prototype.trimEnd;
const hasNativeIsSafeInteger = !!Number.isSafeInteger;
const isSafeInteger = hasNativeIsSafeInteger
    ? Number.isSafeInteger
    : (n) => {
        return (typeof n === 'number' &&
            isFinite(n) &&
            Math.floor(n) === n &&
            Math.abs(n) <= 0x1fffffffffffff);
    };
// IE11 does not support y and u.
let REGEX_SUPPORTS_U_AND_Y = true;
try {
    const re = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    const result = re.exec('a');
    /**
     * legacy Edge or Xbox One browser
     * Unicode flag support: supported
     * Pattern_Syntax support: not supported
     * See https://github.com/formatjs/formatjs/issues/2822
     */
    REGEX_SUPPORTS_U_AND_Y = (result && (result[0] === 'a')) ? true : false;
}
catch (_) {
    REGEX_SUPPORTS_U_AND_Y = false;
}
const startsWith = hasNativeStartsWith
    ? // Native
        function startsWith(s, search, position) {
            return s.startsWith(search, position);
        }
    : // For IE11
        function startsWith(s, search, position) {
            return s.slice(position, position + search.length) === search;
        };
const fromCodePoint = hasNativeFromCodePoint
    ? String.fromCodePoint
    : // IE11
        function fromCodePoint(...codePoints) {
            let elements = '';
            let length = codePoints.length;
            let i = 0;
            let code;
            while (length > i) {
                code = codePoints[i++];
                if (code > 0x10ffff)
                    throw RangeError(code + ' is not a valid code point');
                elements +=
                    code < 0x10000
                        ? String.fromCharCode(code)
                        : String.fromCharCode(((code -= 0x10000) >> 10) + 0xd800, (code % 0x400) + 0xdc00);
            }
            return elements;
        };
const fromEntries = 
// native
hasNativeFromEntries
    ? Object.fromEntries
    : // Ponyfill
        function fromEntries(entries) {
            const obj = {};
            for (const [k, v] of entries) {
                obj[k] = v;
            }
            return obj;
        };
const codePointAt = hasNativeCodePointAt
    ? // Native
        function codePointAt(s, index) {
            return s.codePointAt(index);
        }
    : // IE 11
        function codePointAt(s, index) {
            let size = s.length;
            if (index < 0 || index >= size) {
                return undefined;
            }
            let first = s.charCodeAt(index);
            let second;
            return first < 0xd800 ||
                first > 0xdbff ||
                index + 1 === size ||
                (second = s.charCodeAt(index + 1)) < 0xdc00 ||
                second > 0xdfff
                ? first
                : ((first - 0xd800) << 10) + (second - 0xdc00) + 0x10000;
        };
const trimStart = hasTrimStart
    ? // Native
        function trimStart(s) {
            return s.trimStart();
        }
    : // Ponyfill
        function trimStart(s) {
            return s.replace(SPACE_SEPARATOR_START_REGEX, '');
        };
const trimEnd = hasTrimEnd
    ? // Native
        function trimEnd(s) {
            return s.trimEnd();
        }
    : // Ponyfill
        function trimEnd(s) {
            return s.replace(SPACE_SEPARATOR_END_REGEX, '');
        };
// Prevent minifier to translate new RegExp to literal form that might cause syntax error on IE11.
function RE(s, flag) {
    return new RegExp(s, flag);
}
// #endregion
let matchIdentifierAtIndex;
if (REGEX_SUPPORTS_U_AND_Y) {
    // Native
    const IDENTIFIER_PREFIX_RE = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        IDENTIFIER_PREFIX_RE.lastIndex = index;
        const match = IDENTIFIER_PREFIX_RE.exec(s);
        return (match[1] !== null && match[1] !== undefined) ? match[1] : '';
    };
}
else {
    // IE11
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        let match = [];
        while (true) {
            const c = codePointAt(s, index);
            if (c === undefined || _isWhiteSpace(c) || _isPatternSyntax(c)) {
                break;
            }
            match.push(c);
            index += c >= 0x10000 ? 2 : 1;
        }
        return fromCodePoint(...match);
    };
}
class Parser {
    constructor(message, options = {}) {
        this.message = message;
        this.position = { offset: 0, line: 1, column: 1 };
        this.ignoreTag = !!options.ignoreTag;
        this.requiresOtherClause = !!options.requiresOtherClause;
        this.shouldParseSkeletons = !!options.shouldParseSkeletons;
    }
    parse() {
        if (this.offset() !== 0) {
            throw Error('parser can only be used once');
        }
        return this.parseMessage(0, '', false);
    }
    parseMessage(nestingLevel, parentArgType, expectingCloseTag) {
        let elements = [];
        while (!this.isEOF()) {
            const char = this.char();
            if (char === 123 /* `{` */) {
                const result = this.parseArgument(nestingLevel, expectingCloseTag);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else if (char === 125 /* `}` */ && nestingLevel > 0) {
                break;
            }
            else if (char === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) {
                const position = this.clonePosition();
                this.bump();
                elements.push({
                    type: TYPE.pound,
                    location: createLocation(position, this.clonePosition()),
                });
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                this.peek() === 47 // char code for '/'
            ) {
                if (expectingCloseTag) {
                    break;
                }
                else {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(this.clonePosition(), this.clonePosition()));
                }
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                _isAlpha(this.peek() || 0)) {
                const result = this.parseTag(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else {
                const result = this.parseLiteral(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
        }
        return { val: elements, err: null };
    }
    /**
     * A tag name must start with an ASCII lower/upper case letter. The grammar is based on the
     * [custom element name][] except that a dash is NOT always mandatory and uppercase letters
     * are accepted:
     *
     * ```
     * tag ::= "<" tagName (whitespace)* "/>" | "<" tagName (whitespace)* ">" message "</" tagName (whitespace)* ">"
     * tagName ::= [a-z] (PENChar)*
     * PENChar ::=
     *     "-" | "." | [0-9] | "_" | [a-z] | [A-Z] | #xB7 | [#xC0-#xD6] | [#xD8-#xF6] | [#xF8-#x37D] |
     *     [#x37F-#x1FFF] | [#x200C-#x200D] | [#x203F-#x2040] | [#x2070-#x218F] | [#x2C00-#x2FEF] |
     *     [#x3001-#xD7FF] | [#xF900-#xFDCF] | [#xFDF0-#xFFFD] | [#x10000-#xEFFFF]
     * ```
     *
     * [custom element name]: https://html.spec.whatwg.org/multipage/custom-elements.html#valid-custom-element-name
     * NOTE: We're a bit more lax here since HTML technically does not allow uppercase HTML element but we do
     * since other tag-based engines like React allow it
     */
    parseTag(nestingLevel, parentArgType) {
        const startPosition = this.clonePosition();
        this.bump(); // `<`
        const tagName = this.parseTagName();
        this.bumpSpace();
        if (this.bumpIf('/>')) {
            // Self closing tag
            return {
                val: {
                    type: TYPE.literal,
                    value: `<${tagName}/>`,
                    location: createLocation(startPosition, this.clonePosition()),
                },
                err: null,
            };
        }
        else if (this.bumpIf('>')) {
            const childrenResult = this.parseMessage(nestingLevel + 1, parentArgType, true);
            if (childrenResult.err) {
                return childrenResult;
            }
            const children = childrenResult.val;
            // Expecting a close tag
            const endTagStartPosition = this.clonePosition();
            if (this.bumpIf('</')) {
                if (this.isEOF() || !_isAlpha(this.char())) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                const closingTagNameStartPosition = this.clonePosition();
                const closingTagName = this.parseTagName();
                if (tagName !== closingTagName) {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(closingTagNameStartPosition, this.clonePosition()));
                }
                this.bumpSpace();
                if (!this.bumpIf('>')) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                return {
                    val: {
                        type: TYPE.tag,
                        value: tagName,
                        children,
                        location: createLocation(startPosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            else {
                return this.error(ErrorKind.UNCLOSED_TAG, createLocation(startPosition, this.clonePosition()));
            }
        }
        else {
            return this.error(ErrorKind.INVALID_TAG, createLocation(startPosition, this.clonePosition()));
        }
    }
    /**
     * This method assumes that the caller has peeked ahead for the first tag character.
     */
    parseTagName() {
        const startOffset = this.offset();
        this.bump(); // the first tag name character
        while (!this.isEOF() && _isPotentialElementNameChar(this.char())) {
            this.bump();
        }
        return this.message.slice(startOffset, this.offset());
    }
    parseLiteral(nestingLevel, parentArgType) {
        const start = this.clonePosition();
        let value = '';
        while (true) {
            const parseQuoteResult = this.tryParseQuote(parentArgType);
            if (parseQuoteResult) {
                value += parseQuoteResult;
                continue;
            }
            const parseUnquotedResult = this.tryParseUnquoted(nestingLevel, parentArgType);
            if (parseUnquotedResult) {
                value += parseUnquotedResult;
                continue;
            }
            const parseLeftAngleResult = this.tryParseLeftAngleBracket();
            if (parseLeftAngleResult) {
                value += parseLeftAngleResult;
                continue;
            }
            break;
        }
        const location = createLocation(start, this.clonePosition());
        return {
            val: { type: TYPE.literal, value, location },
            err: null,
        };
    }
    tryParseLeftAngleBracket() {
        if (!this.isEOF() &&
            this.char() === 60 /* `<` */ &&
            (this.ignoreTag ||
                // If at the opening tag or closing tag position, bail.
                !_isAlphaOrSlash(this.peek() || 0))) {
            this.bump(); // `<`
            return '<';
        }
        return null;
    }
    /**
     * Starting with ICU 4.8, an ASCII apostrophe only starts quoted text if it immediately precedes
     * a character that requires quoting (that is, "only where needed"), and works the same in
     * nested messages as on the top level of the pattern. The new behavior is otherwise compatible.
     */
    tryParseQuote(parentArgType) {
        if (this.isEOF() || this.char() !== 39 /* `'` */) {
            return null;
        }
        // Parse escaped char following the apostrophe, or early return if there is no escaped char.
        // Check if is valid escaped character
        switch (this.peek()) {
            case 39 /* `'` */:
                // double quote, should return as a single quote.
                this.bump();
                this.bump();
                return "'";
            // '{', '<', '>', '}'
            case 123:
            case 60:
            case 62:
            case 125:
                break;
            case 35: // '#'
                if (parentArgType === 'plural' || parentArgType === 'selectordinal') {
                    break;
                }
                return null;
            default:
                return null;
        }
        this.bump(); // apostrophe
        const codePoints = [this.char()]; // escaped char
        this.bump();
        // read chars until the optional closing apostrophe is found
        while (!this.isEOF()) {
            const ch = this.char();
            if (ch === 39 /* `'` */) {
                if (this.peek() === 39 /* `'` */) {
                    codePoints.push(39);
                    // Bump one more time because we need to skip 2 characters.
                    this.bump();
                }
                else {
                    // Optional closing apostrophe.
                    this.bump();
                    break;
                }
            }
            else {
                codePoints.push(ch);
            }
            this.bump();
        }
        return fromCodePoint(...codePoints);
    }
    tryParseUnquoted(nestingLevel, parentArgType) {
        if (this.isEOF()) {
            return null;
        }
        const ch = this.char();
        if (ch === 60 /* `<` */ ||
            ch === 123 /* `{` */ ||
            (ch === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) ||
            (ch === 125 /* `}` */ && nestingLevel > 0)) {
            return null;
        }
        else {
            this.bump();
            return fromCodePoint(ch);
        }
    }
    parseArgument(nestingLevel, expectingCloseTag) {
        const openingBracePosition = this.clonePosition();
        this.bump(); // `{`
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        if (this.char() === 125 /* `}` */) {
            this.bump();
            return this.error(ErrorKind.EMPTY_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        // argument name
        let value = this.parseIdentifierIfPossible().value;
        if (!value) {
            return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        switch (this.char()) {
            // Simple argument: `{name}`
            case 125 /* `}` */: {
                this.bump(); // `}`
                return {
                    val: {
                        type: TYPE.argument,
                        // value does not include the opening and closing braces.
                        value,
                        location: createLocation(openingBracePosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            // Argument with options: `{name, format, ...}`
            case 44 /* `,` */: {
                this.bump(); // `,`
                this.bumpSpace();
                if (this.isEOF()) {
                    return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
                }
                return this.parseArgumentOptions(nestingLevel, expectingCloseTag, value, openingBracePosition);
            }
            default:
                return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
    }
    /**
     * Advance the parser until the end of the identifier, if it is currently on
     * an identifier character. Return an empty string otherwise.
     */
    parseIdentifierIfPossible() {
        const startingPosition = this.clonePosition();
        const startOffset = this.offset();
        const value = matchIdentifierAtIndex(this.message, startOffset);
        const endOffset = startOffset + value.length;
        this.bumpTo(endOffset);
        const endPosition = this.clonePosition();
        const location = createLocation(startingPosition, endPosition);
        return { value, location };
    }
    parseArgumentOptions(nestingLevel, expectingCloseTag, value, openingBracePosition) {
        // Parse this range:
        // {name, type, style}
        //        ^---^
        let typeStartPosition = this.clonePosition();
        let argType = this.parseIdentifierIfPossible().value;
        let typeEndPosition = this.clonePosition();
        switch (argType) {
            case '':
                // Expecting a style string number, date, time, plural, selectordinal, or select.
                return this.error(ErrorKind.EXPECT_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
            case 'number':
            case 'price':
            case 'date':
            case 'time': {
                // Parse this range:
                // {name, number, style}
                //              ^-------^
                this.bumpSpace();
                let styleAndLocation = null;
                if (this.bumpIf(',')) {
                    this.bumpSpace();
                    const styleStartPosition = this.clonePosition();
                    const result = this.parseSimpleArgStyleIfPossible();
                    if (result.err) {
                        return result;
                    }
                    const style = trimEnd(result.val);
                    if (style.length === 0) {
                        return this.error(ErrorKind.EXPECT_ARGUMENT_STYLE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    const styleLocation = createLocation(styleStartPosition, this.clonePosition());
                    styleAndLocation = { style, styleLocation };
                }
                const argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                const location = createLocation(openingBracePosition, this.clonePosition());
                // Extract style or skeleton
                if (styleAndLocation && styleAndLocation.style && startsWith(styleAndLocation.style, '::', 0)) {
                    // Skeleton starts with `::`.
                    const skeleton = trimStart(styleAndLocation.style.slice(2));
                    if (argType === 'number') {
                        const result = this.parseNumberSkeletonFromString(skeleton, styleAndLocation.styleLocation);
                        if (result.err) {
                            return result;
                        }
                        return {
                            val: { type: TYPE.number, value, location, style: result.val },
                            err: null,
                        };
                    }
                    else {
                        if (skeleton.length === 0) {
                            return this.error(ErrorKind.EXPECT_DATE_TIME_SKELETON, location);
                        }
                        const style = {
                            type: SKELETON_TYPE.dateTime,
                            pattern: skeleton,
                            location: styleAndLocation.styleLocation,
                            parsedOptions: this.shouldParseSkeletons
                                ? parseDateTimeSkeleton(skeleton)
                                : {},
                        };
                        const type = argType === 'date' ? TYPE.date : TYPE.time;
                        return {
                            val: { type, value, location, style },
                            err: null,
                        };
                    }
                }
                // Regular style or no style.
                const style = (styleAndLocation && styleAndLocation.style) ? styleAndLocation.style : null;
                return {
                    val: {
                        type: argType === 'number'
                            ? TYPE.number
                            : argType === 'price'
                                ? TYPE.price
                                : argType === 'date'
                                    ? TYPE.date
                                    : TYPE.time,
                        value,
                        location,
                        style: style,
                    },
                    err: null,
                };
            }
            case 'plural':
            case 'selectordinal':
            case 'select': {
                // Parse this range:
                // {name, plural, options}
                //              ^---------^
                const typeEndPosition = this.clonePosition();
                this.bumpSpace();
                if (!this.bumpIf(',')) {
                    return this.error(ErrorKind.EXPECT_SELECT_ARGUMENT_OPTIONS, createLocation(typeEndPosition, Object.assign({}, typeEndPosition)));
                }
                this.bumpSpace();
                // Parse offset:
                // {name, plural, offset:1, options}
                //                ^-----^
                //
                // or the first option:
                //
                // {name, plural, one {...} other {...}}
                //                ^--^
                let identifierAndLocation = this.parseIdentifierIfPossible();
                let pluralOffset = 0;
                if (argType !== 'select' && identifierAndLocation.value === 'offset') {
                    if (!this.bumpIf(':')) {
                        return this.error(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    this.bumpSpace();
                    const result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, ErrorKind.INVALID_PLURAL_ARGUMENT_OFFSET_VALUE);
                    if (result.err) {
                        return result;
                    }
                    // Parse another identifier for option parsing
                    this.bumpSpace();
                    identifierAndLocation = this.parseIdentifierIfPossible();
                    pluralOffset = result.val;
                }
                const optionsResult = this.tryParsePluralOrSelectOptions(nestingLevel, argType, expectingCloseTag, identifierAndLocation);
                if (optionsResult.err) {
                    return optionsResult;
                }
                const argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                const location = createLocation(openingBracePosition, this.clonePosition());
                if (argType === 'select') {
                    return {
                        val: {
                            type: TYPE.select,
                            value,
                            options: fromEntries(optionsResult.val),
                            location,
                        },
                        err: null,
                    };
                }
                else {
                    return {
                        val: {
                            type: TYPE.plural,
                            value,
                            options: fromEntries(optionsResult.val),
                            offset: pluralOffset,
                            pluralType: argType === 'plural' ? 'cardinal' : 'ordinal',
                            location,
                        },
                        err: null,
                    };
                }
            }
            default:
                return this.error(ErrorKind.INVALID_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
        }
    }
    tryParseArgumentClose(openingBracePosition) {
        // Parse: {value, number, ::currency/GBP }
        //
        if (this.isEOF() || this.char() !== 125 /* `}` */) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bump(); // `}`
        return { val: true, err: null };
    }
    /**
     * See: https://github.com/unicode-org/icu/blob/af7ed1f6d2298013dc303628438ec4abe1f16479/icu4c/source/common/messagepattern.cpp#L659
     */
    parseSimpleArgStyleIfPossible() {
        let nestedBraces = 0;
        const startPosition = this.clonePosition();
        while (!this.isEOF()) {
            const ch = this.char();
            switch (ch) {
                case 39 /* `'` */: {
                    // Treat apostrophe as quoting but include it in the style part.
                    // Find the end of the quoted literal text.
                    this.bump();
                    let apostrophePosition = this.clonePosition();
                    if (!this.bumpUntil("'")) {
                        return this.error(ErrorKind.UNCLOSED_QUOTE_IN_ARGUMENT_STYLE, createLocation(apostrophePosition, this.clonePosition()));
                    }
                    this.bump();
                    break;
                }
                case 123 /* `{` */: {
                    nestedBraces += 1;
                    this.bump();
                    break;
                }
                case 125 /* `}` */: {
                    if (nestedBraces > 0) {
                        nestedBraces -= 1;
                    }
                    else {
                        return {
                            val: this.message.slice(startPosition.offset, this.offset()),
                            err: null,
                        };
                    }
                    break;
                }
                default:
                    this.bump();
                    break;
            }
        }
        return {
            val: this.message.slice(startPosition.offset, this.offset()),
            err: null,
        };
    }
    parseNumberSkeletonFromString(skeleton, location) {
        let tokens = [];
        try {
            tokens = parseNumberSkeletonFromString(skeleton);
        }
        catch (e) {
            return this.error(ErrorKind.INVALID_NUMBER_SKELETON, location);
        }
        return {
            val: {
                type: SKELETON_TYPE.number,
                tokens,
                location,
                parsedOptions: this.shouldParseSkeletons
                    ? parseNumberSkeleton(tokens)
                    : {},
            },
            err: null,
        };
    }
    /**
     * @param nesting_level The current nesting level of messages.
     *     This can be positive when parsing message fragment in select or plural argument options.
     * @param parent_arg_type The parent argument's type.
     * @param parsed_first_identifier If provided, this is the first identifier-like selector of
     *     the argument. It is a by-product of a previous parsing attempt.
     * @param expecting_close_tag If true, this message is directly or indirectly nested inside
     *     between a pair of opening and closing tags. The nested message will not parse beyond
     *     the closing tag boundary.
     */
    tryParsePluralOrSelectOptions(nestingLevel, parentArgType, expectCloseTag, parsedFirstIdentifier) {
        let hasOtherClause = false;
        const options = [];
        const parsedSelectors = new Set();
        let { value: selector, location: selectorLocation } = parsedFirstIdentifier;
        // Parse:
        // one {one apple}
        // ^--^
        while (true) {
            if (selector.length === 0) {
                const startPosition = this.clonePosition();
                if (parentArgType !== 'select' && this.bumpIf('=')) {
                    // Try parse `={number}` selector
                    const result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, ErrorKind.INVALID_PLURAL_ARGUMENT_SELECTOR);
                    if (result.err) {
                        return result;
                    }
                    selectorLocation = createLocation(startPosition, this.clonePosition());
                    selector = this.message.slice(startPosition.offset, this.offset());
                }
                else {
                    break;
                }
            }
            // Duplicate selector clauses
            if (parsedSelectors.has(selector)) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.DUPLICATE_SELECT_ARGUMENT_SELECTOR
                    : ErrorKind.DUPLICATE_PLURAL_ARGUMENT_SELECTOR, selectorLocation);
            }
            if (selector === 'other') {
                hasOtherClause = true;
            }
            // Parse:
            // one {one apple}
            //     ^----------^
            this.bumpSpace();
            const openingBracePosition = this.clonePosition();
            if (!this.bumpIf('{')) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT
                    : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT, createLocation(this.clonePosition(), this.clonePosition()));
            }
            const fragmentResult = this.parseMessage(nestingLevel + 1, parentArgType, expectCloseTag);
            if (fragmentResult.err) {
                return fragmentResult;
            }
            const argCloseResult = this.tryParseArgumentClose(openingBracePosition);
            if (argCloseResult.err) {
                return argCloseResult;
            }
            options.push([
                selector,
                {
                    value: fragmentResult.val,
                    location: createLocation(openingBracePosition, this.clonePosition()),
                },
            ]);
            // Keep track of the existing selectors
            parsedSelectors.add(selector);
            // Prep next selector clause.
            this.bumpSpace();
            ({
                value: selector,
                location: selectorLocation,
            } = this.parseIdentifierIfPossible());
        }
        if (options.length === 0) {
            return this.error(parentArgType === 'select'
                ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR
                : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, createLocation(this.clonePosition(), this.clonePosition()));
        }
        if (this.requiresOtherClause && !hasOtherClause) {
            return this.error(ErrorKind.MISSING_OTHER_CLAUSE, createLocation(this.clonePosition(), this.clonePosition()));
        }
        return { val: options, err: null };
    }
    tryParseDecimalInteger(expectNumberError, invalidNumberError) {
        let sign = 1;
        const startingPosition = this.clonePosition();
        if (this.bumpIf('+')) ;
        else if (this.bumpIf('-')) {
            sign = -1;
        }
        let hasDigits = false;
        let decimal = 0;
        while (!this.isEOF()) {
            const ch = this.char();
            if (ch >= 48 /* `0` */ && ch <= 57 /* `9` */) {
                hasDigits = true;
                decimal = decimal * 10 + (ch - 48);
                this.bump();
            }
            else {
                break;
            }
        }
        const location = createLocation(startingPosition, this.clonePosition());
        if (!hasDigits) {
            return this.error(expectNumberError, location);
        }
        decimal *= sign;
        if (!isSafeInteger(decimal)) {
            return this.error(invalidNumberError, location);
        }
        return { val: decimal, err: null };
    }
    offset() {
        return this.position.offset;
    }
    isEOF() {
        return this.offset() === this.message.length;
    }
    clonePosition() {
        // This is much faster than `Object.assign` or spread.
        return {
            offset: this.position.offset,
            line: this.position.line,
            column: this.position.column,
        };
    }
    /**
     * Return the code point at the current position of the parser.
     * Throws if the index is out of bound.
     */
    char() {
        const offset = this.position.offset;
        if (offset >= this.message.length) {
            throw Error('out of bound');
        }
        const code = codePointAt(this.message, offset);
        if (code === undefined) {
            throw Error(`Offset ${offset} is at invalid UTF-16 code unit boundary`);
        }
        return code;
    }
    error(kind, location) {
        return {
            val: null,
            err: {
                kind,
                message: this.message,
                location,
            },
        };
    }
    /** Bump the parser to the next UTF-16 code unit. */
    bump() {
        if (this.isEOF()) {
            return;
        }
        const code = this.char();
        if (code === 10 /* '\n' */) {
            this.position.line += 1;
            this.position.column = 1;
            this.position.offset += 1;
        }
        else {
            this.position.column += 1;
            // 0 ~ 0x10000 -> unicode BMP, otherwise skip the surrogate pair.
            this.position.offset += code < 0x10000 ? 1 : 2;
        }
    }
    /**
     * If the substring starting at the current position of the parser has
     * the given prefix, then bump the parser to the character immediately
     * following the prefix and return true. Otherwise, don't bump the parser
     * and return false.
     */
    bumpIf(prefix) {
        if (startsWith(this.message, prefix, this.offset())) {
            for (let i = 0; i < prefix.length; i++) {
                this.bump();
            }
            return true;
        }
        return false;
    }
    /**
     * Bump the parser until the pattern character is found and return `true`.
     * Otherwise bump to the end of the file and return `false`.
     */
    bumpUntil(pattern) {
        const currentOffset = this.offset();
        const index = this.message.indexOf(pattern, currentOffset);
        if (index >= 0) {
            this.bumpTo(index);
            return true;
        }
        else {
            this.bumpTo(this.message.length);
            return false;
        }
    }
    /**
     * Bump the parser to the target offset.
     * If target offset is beyond the end of the input, bump the parser to the end of the input.
     */
    bumpTo(targetOffset) {
        if (this.offset() > targetOffset) {
            throw Error(`targetOffset ${targetOffset} must be greater than or equal to the current offset ${this.offset()}`);
        }
        targetOffset = Math.min(targetOffset, this.message.length);
        while (true) {
            const offset = this.offset();
            if (offset === targetOffset) {
                break;
            }
            if (offset > targetOffset) {
                throw Error(`targetOffset ${targetOffset} is at invalid UTF-16 code unit boundary`);
            }
            this.bump();
            if (this.isEOF()) {
                break;
            }
        }
    }
    /** advance the parser through all whitespace to the next non-whitespace code unit. */
    bumpSpace() {
        while (!this.isEOF() && _isWhiteSpace(this.char())) {
            this.bump();
        }
    }
    /**
     * Peek at the *next* Unicode codepoint in the input without advancing the parser.
     * If the input has been exhausted, then this returns null.
     */
    peek() {
        if (this.isEOF()) {
            return null;
        }
        const code = this.char();
        const offset = this.offset();
        const nextCode = this.message.charCodeAt(offset + (code >= 0x10000 ? 2 : 1));
        return (nextCode !== null && nextCode !== undefined) ? nextCode : null;
    }
}
/**
 * This check if codepoint is alphabet (lower & uppercase)
 * @param codepoint
 * @returns
 */
function _isAlpha(codepoint) {
    return ((codepoint >= 97 && codepoint <= 122) ||
        (codepoint >= 65 && codepoint <= 90));
}
function _isAlphaOrSlash(codepoint) {
    return _isAlpha(codepoint) || codepoint === 47; /* '/' */
}
/** See `parseTag` function docs. */
function _isPotentialElementNameChar(c) {
    return (c === 45 /* '-' */ ||
        c === 46 /* '.' */ ||
        (c >= 48 && c <= 57) /* 0..9 */ ||
        c === 95 /* '_' */ ||
        (c >= 97 && c <= 122) /** a..z */ ||
        (c >= 65 && c <= 90) /* A..Z */ ||
        c == 0xb7 ||
        (c >= 0xc0 && c <= 0xd6) ||
        (c >= 0xd8 && c <= 0xf6) ||
        (c >= 0xf8 && c <= 0x37d) ||
        (c >= 0x37f && c <= 0x1fff) ||
        (c >= 0x200c && c <= 0x200d) ||
        (c >= 0x203f && c <= 0x2040) ||
        (c >= 0x2070 && c <= 0x218f) ||
        (c >= 0x2c00 && c <= 0x2fef) ||
        (c >= 0x3001 && c <= 0xd7ff) ||
        (c >= 0xf900 && c <= 0xfdcf) ||
        (c >= 0xfdf0 && c <= 0xfffd) ||
        (c >= 0x10000 && c <= 0xeffff));
}
/**
 * Code point equivalent of regex `\p{White_Space}`.
 * From: https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isWhiteSpace(c) {
    return ((c >= 0x0009 && c <= 0x000d) ||
        c === 0x0020 ||
        c === 0x0085 ||
        (c >= 0x200e && c <= 0x200f) ||
        c === 0x2028 ||
        c === 0x2029);
}
/**
 * Code point equivalent of regex `\p{Pattern_Syntax}`.
 * See https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isPatternSyntax(c) {
    return ((c >= 0x0021 && c <= 0x0023) ||
        c === 0x0024 ||
        (c >= 0x0025 && c <= 0x0027) ||
        c === 0x0028 ||
        c === 0x0029 ||
        c === 0x002a ||
        c === 0x002b ||
        c === 0x002c ||
        c === 0x002d ||
        (c >= 0x002e && c <= 0x002f) ||
        (c >= 0x003a && c <= 0x003b) ||
        (c >= 0x003c && c <= 0x003e) ||
        (c >= 0x003f && c <= 0x0040) ||
        c === 0x005b ||
        c === 0x005c ||
        c === 0x005d ||
        c === 0x005e ||
        c === 0x0060 ||
        c === 0x007b ||
        c === 0x007c ||
        c === 0x007d ||
        c === 0x007e ||
        c === 0x00a1 ||
        (c >= 0x00a2 && c <= 0x00a5) ||
        c === 0x00a6 ||
        c === 0x00a7 ||
        c === 0x00a9 ||
        c === 0x00ab ||
        c === 0x00ac ||
        c === 0x00ae ||
        c === 0x00b0 ||
        c === 0x00b1 ||
        c === 0x00b6 ||
        c === 0x00bb ||
        c === 0x00bf ||
        c === 0x00d7 ||
        c === 0x00f7 ||
        (c >= 0x2010 && c <= 0x2015) ||
        (c >= 0x2016 && c <= 0x2017) ||
        c === 0x2018 ||
        c === 0x2019 ||
        c === 0x201a ||
        (c >= 0x201b && c <= 0x201c) ||
        c === 0x201d ||
        c === 0x201e ||
        c === 0x201f ||
        (c >= 0x2020 && c <= 0x2027) ||
        (c >= 0x2030 && c <= 0x2038) ||
        c === 0x2039 ||
        c === 0x203a ||
        (c >= 0x203b && c <= 0x203e) ||
        (c >= 0x2041 && c <= 0x2043) ||
        c === 0x2044 ||
        c === 0x2045 ||
        c === 0x2046 ||
        (c >= 0x2047 && c <= 0x2051) ||
        c === 0x2052 ||
        c === 0x2053 ||
        (c >= 0x2055 && c <= 0x205e) ||
        (c >= 0x2190 && c <= 0x2194) ||
        (c >= 0x2195 && c <= 0x2199) ||
        (c >= 0x219a && c <= 0x219b) ||
        (c >= 0x219c && c <= 0x219f) ||
        c === 0x21a0 ||
        (c >= 0x21a1 && c <= 0x21a2) ||
        c === 0x21a3 ||
        (c >= 0x21a4 && c <= 0x21a5) ||
        c === 0x21a6 ||
        (c >= 0x21a7 && c <= 0x21ad) ||
        c === 0x21ae ||
        (c >= 0x21af && c <= 0x21cd) ||
        (c >= 0x21ce && c <= 0x21cf) ||
        (c >= 0x21d0 && c <= 0x21d1) ||
        c === 0x21d2 ||
        c === 0x21d3 ||
        c === 0x21d4 ||
        (c >= 0x21d5 && c <= 0x21f3) ||
        (c >= 0x21f4 && c <= 0x22ff) ||
        (c >= 0x2300 && c <= 0x2307) ||
        c === 0x2308 ||
        c === 0x2309 ||
        c === 0x230a ||
        c === 0x230b ||
        (c >= 0x230c && c <= 0x231f) ||
        (c >= 0x2320 && c <= 0x2321) ||
        (c >= 0x2322 && c <= 0x2328) ||
        c === 0x2329 ||
        c === 0x232a ||
        (c >= 0x232b && c <= 0x237b) ||
        c === 0x237c ||
        (c >= 0x237d && c <= 0x239a) ||
        (c >= 0x239b && c <= 0x23b3) ||
        (c >= 0x23b4 && c <= 0x23db) ||
        (c >= 0x23dc && c <= 0x23e1) ||
        (c >= 0x23e2 && c <= 0x2426) ||
        (c >= 0x2427 && c <= 0x243f) ||
        (c >= 0x2440 && c <= 0x244a) ||
        (c >= 0x244b && c <= 0x245f) ||
        (c >= 0x2500 && c <= 0x25b6) ||
        c === 0x25b7 ||
        (c >= 0x25b8 && c <= 0x25c0) ||
        c === 0x25c1 ||
        (c >= 0x25c2 && c <= 0x25f7) ||
        (c >= 0x25f8 && c <= 0x25ff) ||
        (c >= 0x2600 && c <= 0x266e) ||
        c === 0x266f ||
        (c >= 0x2670 && c <= 0x2767) ||
        c === 0x2768 ||
        c === 0x2769 ||
        c === 0x276a ||
        c === 0x276b ||
        c === 0x276c ||
        c === 0x276d ||
        c === 0x276e ||
        c === 0x276f ||
        c === 0x2770 ||
        c === 0x2771 ||
        c === 0x2772 ||
        c === 0x2773 ||
        c === 0x2774 ||
        c === 0x2775 ||
        (c >= 0x2794 && c <= 0x27bf) ||
        (c >= 0x27c0 && c <= 0x27c4) ||
        c === 0x27c5 ||
        c === 0x27c6 ||
        (c >= 0x27c7 && c <= 0x27e5) ||
        c === 0x27e6 ||
        c === 0x27e7 ||
        c === 0x27e8 ||
        c === 0x27e9 ||
        c === 0x27ea ||
        c === 0x27eb ||
        c === 0x27ec ||
        c === 0x27ed ||
        c === 0x27ee ||
        c === 0x27ef ||
        (c >= 0x27f0 && c <= 0x27ff) ||
        (c >= 0x2800 && c <= 0x28ff) ||
        (c >= 0x2900 && c <= 0x2982) ||
        c === 0x2983 ||
        c === 0x2984 ||
        c === 0x2985 ||
        c === 0x2986 ||
        c === 0x2987 ||
        c === 0x2988 ||
        c === 0x2989 ||
        c === 0x298a ||
        c === 0x298b ||
        c === 0x298c ||
        c === 0x298d ||
        c === 0x298e ||
        c === 0x298f ||
        c === 0x2990 ||
        c === 0x2991 ||
        c === 0x2992 ||
        c === 0x2993 ||
        c === 0x2994 ||
        c === 0x2995 ||
        c === 0x2996 ||
        c === 0x2997 ||
        c === 0x2998 ||
        (c >= 0x2999 && c <= 0x29d7) ||
        c === 0x29d8 ||
        c === 0x29d9 ||
        c === 0x29da ||
        c === 0x29db ||
        (c >= 0x29dc && c <= 0x29fb) ||
        c === 0x29fc ||
        c === 0x29fd ||
        (c >= 0x29fe && c <= 0x2aff) ||
        (c >= 0x2b00 && c <= 0x2b2f) ||
        (c >= 0x2b30 && c <= 0x2b44) ||
        (c >= 0x2b45 && c <= 0x2b46) ||
        (c >= 0x2b47 && c <= 0x2b4c) ||
        (c >= 0x2b4d && c <= 0x2b73) ||
        (c >= 0x2b74 && c <= 0x2b75) ||
        (c >= 0x2b76 && c <= 0x2b95) ||
        c === 0x2b96 ||
        (c >= 0x2b97 && c <= 0x2bff) ||
        (c >= 0x2e00 && c <= 0x2e01) ||
        c === 0x2e02 ||
        c === 0x2e03 ||
        c === 0x2e04 ||
        c === 0x2e05 ||
        (c >= 0x2e06 && c <= 0x2e08) ||
        c === 0x2e09 ||
        c === 0x2e0a ||
        c === 0x2e0b ||
        c === 0x2e0c ||
        c === 0x2e0d ||
        (c >= 0x2e0e && c <= 0x2e16) ||
        c === 0x2e17 ||
        (c >= 0x2e18 && c <= 0x2e19) ||
        c === 0x2e1a ||
        c === 0x2e1b ||
        c === 0x2e1c ||
        c === 0x2e1d ||
        (c >= 0x2e1e && c <= 0x2e1f) ||
        c === 0x2e20 ||
        c === 0x2e21 ||
        c === 0x2e22 ||
        c === 0x2e23 ||
        c === 0x2e24 ||
        c === 0x2e25 ||
        c === 0x2e26 ||
        c === 0x2e27 ||
        c === 0x2e28 ||
        c === 0x2e29 ||
        (c >= 0x2e2a && c <= 0x2e2e) ||
        c === 0x2e2f ||
        (c >= 0x2e30 && c <= 0x2e39) ||
        (c >= 0x2e3a && c <= 0x2e3b) ||
        (c >= 0x2e3c && c <= 0x2e3f) ||
        c === 0x2e40 ||
        c === 0x2e41 ||
        c === 0x2e42 ||
        (c >= 0x2e43 && c <= 0x2e4f) ||
        (c >= 0x2e50 && c <= 0x2e51) ||
        c === 0x2e52 ||
        (c >= 0x2e53 && c <= 0x2e7f) ||
        (c >= 0x3001 && c <= 0x3003) ||
        c === 0x3008 ||
        c === 0x3009 ||
        c === 0x300a ||
        c === 0x300b ||
        c === 0x300c ||
        c === 0x300d ||
        c === 0x300e ||
        c === 0x300f ||
        c === 0x3010 ||
        c === 0x3011 ||
        (c >= 0x3012 && c <= 0x3013) ||
        c === 0x3014 ||
        c === 0x3015 ||
        c === 0x3016 ||
        c === 0x3017 ||
        c === 0x3018 ||
        c === 0x3019 ||
        c === 0x301a ||
        c === 0x301b ||
        c === 0x301c ||
        c === 0x301d ||
        (c >= 0x301e && c <= 0x301f) ||
        c === 0x3020 ||
        c === 0x3030 ||
        c === 0xfd3e ||
        c === 0xfd3f ||
        (c >= 0xfe45 && c <= 0xfe46));
}

function pruneLocation(els) {
    els.forEach(el => {
        delete el.location;
        if (isSelectElement(el) || isPluralElement(el)) {
            for (const k in el.options) {
                delete el.options[k].location;
                pruneLocation(el.options[k].value);
            }
        }
        else if (isNumberElement(el) && isNumberSkeleton(el.style)) {
            delete el.style.location;
        }
        else if ((isDateElement(el) || isTimeElement(el)) &&
            isDateTimeSkeleton(el.style)) {
            delete el.style.location;
        }
        else if (isTagElement(el)) {
            pruneLocation(el.children);
        }
    });
}
function parse(message, opts = {}) {
    opts = Object.assign({ shouldParseSkeletons: true, requiresOtherClause: true }, opts);
    const result = new Parser(message, opts).parse();
    if (result.err) {
        const error = SyntaxError(ErrorKind[result.err.kind]);
        // @ts-expect-error Assign to error object
        error.location = result.err.location;
        // @ts-expect-error Assign to error object
        error.originalMessage = result.err.message;
        throw error;
    }
    if (!opts || (opts && !opts.captureLocation)) {
        pruneLocation(result.val);
    }
    return result.val;
}

//
// Main
//
function memoize(fn, options) {
    var cache = options && options.cache ? options.cache : cacheDefault;
    var serializer = options && options.serializer ? options.serializer : serializerDefault;
    var strategy = options && options.strategy ? options.strategy : strategyDefault;
    return strategy(fn, {
        cache: cache,
        serializer: serializer,
    });
}
//
// Strategy
//
function isPrimitive(value) {
    return (value == null || typeof value === 'number' || typeof value === 'boolean'); // || typeof value === "string" 'unsafe' primitive for our needs
}
function monadic(fn, cache, serializer, arg) {
    var cacheKey = isPrimitive(arg) ? arg : serializer(arg);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.call(this, arg);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function variadic(fn, cache, serializer) {
    var args = Array.prototype.slice.call(arguments, 3);
    var cacheKey = serializer(args);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.apply(this, args);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function assemble(fn, context, strategy, cache, serialize) {
    return strategy.bind(context, fn, cache, serialize);
}
function strategyDefault(fn, options) {
    var strategy = fn.length === 1 ? monadic : variadic;
    return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}
function strategyVariadic(fn, options) {
    return assemble(fn, this, variadic, options.cache.create(), options.serializer);
}
function strategyMonadic(fn, options) {
    return assemble(fn, this, monadic, options.cache.create(), options.serializer);
}
//
// Serializer
//
var serializerDefault = function () {
    return JSON.stringify(arguments);
};
//
// Cache
//
function ObjectWithoutPrototypeCache() {
    this.cache = Object.create(null);
}
ObjectWithoutPrototypeCache.prototype.has = function (key) {
    return key in this.cache;
};
ObjectWithoutPrototypeCache.prototype.get = function (key) {
    return this.cache[key];
};
ObjectWithoutPrototypeCache.prototype.set = function (key, value) {
    this.cache[key] = value;
};
var cacheDefault = {
    create: function create() {
        // @ts-ignore
        return new ObjectWithoutPrototypeCache();
    },
};
var strategies = {
    variadic: strategyVariadic,
    monadic: strategyMonadic,
};

var ErrorCode;
(function (ErrorCode) {
    // When we have a placeholder but no value to format
    ErrorCode["MISSING_VALUE"] = "MISSING_VALUE";
    // When value supplied is invalid
    ErrorCode["INVALID_VALUE"] = "INVALID_VALUE";
    // When we need specific Intl API but it's not available
    ErrorCode["MISSING_INTL_API"] = "MISSING_INTL_API";
})(ErrorCode || (ErrorCode = {}));
class FormatError extends Error {
    constructor(msg, code, originalMessage) {
        super(msg);
        this.code = code;
        this.originalMessage = originalMessage;
    }
    toString() {
        return `[formatjs Error: ${this.code}] ${this.message}`;
    }
}
class InvalidValueError extends FormatError {
    constructor(variableId, value, options, originalMessage) {
        super(`Invalid values for "${variableId}": "${value}". Options are "${Object.keys(options).join('", "')}"`, ErrorCode.INVALID_VALUE, originalMessage);
    }
}
class InvalidValueTypeError extends FormatError {
    constructor(value, type, originalMessage) {
        super(`Value for "${value}" must be of type ${type}`, ErrorCode.INVALID_VALUE, originalMessage);
    }
}
class MissingValueError extends FormatError {
    constructor(variableId, originalMessage) {
        super(`The intl string context variable "${variableId}" was not provided to the string "${originalMessage}"`, ErrorCode.MISSING_VALUE, originalMessage);
    }
}

var PART_TYPE;
(function (PART_TYPE) {
    PART_TYPE[PART_TYPE["literal"] = 0] = "literal";
    PART_TYPE[PART_TYPE["object"] = 1] = "object";
})(PART_TYPE || (PART_TYPE = {}));
function mergeLiteral(parts) {
    if (parts.length < 2) {
        return parts;
    }
    return parts.reduce((all, part) => {
        const lastPart = all[all.length - 1];
        if (!lastPart ||
            lastPart.type !== PART_TYPE.literal ||
            part.type !== PART_TYPE.literal) {
            all.push(part);
        }
        else {
            lastPart.value += part.value;
        }
        return all;
    }, []);
}
function isFormatXMLElementFn(el) {
    return typeof el === 'function';
}
// AMAZON_CHANGE_START
function isFormatArgElementFn(el) {
    return typeof el === 'function';
}
function formatToVdomParts(els, localesOrLocalizationContext, formatters, formats, hyperscriptRuntime, values, currentPluralValue, 
// For debugging
originalMessage) {
    const formattedParts = formatToParts(els, localesOrLocalizationContext, formatters, formats, values, currentPluralValue, originalMessage, hyperscriptRuntime);
    return formattedParts.map(part => {
        return part.value;
    });
}
// AMAZON_CHANGE_END
// TODO(skeleton): add skeleton support
function formatToParts(els, 
// AMAZON_CHANGE_START
localesOrLocalizationContext, 
// AMAZON_CHANGE_END
formatters, formats, values, currentPluralValue, 
// For debugging
originalMessage, 
// AMAZON_CHANGE_START
hyperscriptRuntime
// AMAZON_CHANGE_END
) {
    // Hot path for straight simple msg translations
    if (els.length === 1 && isLiteralElement(els[0])) {
        return [
            {
                type: PART_TYPE.literal,
                value: els[0].value,
            },
        ];
    }
    // AMAZON_CHANGE_START
    let localizationContext;
    let locales;
    if (isLocalizationContext(localesOrLocalizationContext)) {
        localizationContext = localesOrLocalizationContext;
        locales = localesOrLocalizationContext.getLocale();
    }
    else {
        locales = localesOrLocalizationContext;
    }
    // AMAZON_CHANGE_END
    const result = [];
    for (const el of els) {
        // Exit early for string parts.
        if (isLiteralElement(el)) {
            result.push({
                type: PART_TYPE.literal,
                value: el.value,
            });
            continue;
        }
        // TODO: should this part be literal type?
        // Replace `#` in plural rules with the actual numeric value.
        if (isPoundElement(el)) {
            if (typeof currentPluralValue === 'number') {
                result.push({
                    type: PART_TYPE.literal,
                    value: formatters.getNumberFormat(locales).format(currentPluralValue),
                });
            }
            continue;
        }
        const { value: varName } = el;
        // AMAZON_CHANGE_START
        if (!values) {
            throw new MissingValueError(varName, originalMessage);
        }
        // AMAZON_CHANGE_END
        if (isTagElement(el)) {
            // tslint:disable-next-line:no-shadowed-variable
            const { children, value } = el;
            let formatFn = values[value];
            // AMAZON_CHANGE_START
            if (hyperscriptRuntime) {
                // Using identity format function in case of external hyperscript function.
                formatFn = (tokens) => tokens;
            }
            // AMAZON_CHANGE_END
            if (!isFormatXMLElementFn(formatFn)) {
                throw new InvalidValueTypeError(value, 'function', originalMessage);
            }
            const parts = formatToParts(children, 
            // AMAZON_CHANGE_START
            localesOrLocalizationContext, 
            // AMAZON_CHANGE_END
            formatters, formats, values, currentPluralValue, originalMessage, hyperscriptRuntime);
            let chunks = formatFn(parts.map(p => p.value));
            if (!Array.isArray(chunks)) {
                chunks = [chunks];
            }
            // AMAZON_CHANGE_START
            if (hyperscriptRuntime) {
                // Create VDOM for this tag element.
                result.push({
                    type: PART_TYPE.object,
                    value: hyperscriptRuntime.createElement(value, {}, ...chunks),
                });
                // AMAZON_CHANGE_END
            }
            else {
                result.push(...chunks.map((c) => {
                    return {
                        type: typeof c === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                        value: c,
                    };
                }));
            }
            continue;
        }
        // Enforce that all required values are provided by the caller.
        if (!(values && varName in values)) {
            throw new MissingValueError(varName, originalMessage);
        }
        let value = values[varName];
        if (isArgumentElement(el)) {
            if (!value || typeof value === 'string' || typeof value === 'number') {
                value =
                    typeof value === 'string' || typeof value === 'number'
                        ? String(value)
                        : '';
                // AMAZON_CHANGE_START
                // We have received formatting function to style this argument.
            }
            else if (isFormatArgElementFn(value)) {
                value = value(hyperscriptRuntime, {}, []);
            }
            // AMAZON_CHANGE_END
            result.push({
                type: typeof value === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                value,
            });
            continue;
        }
        // Recursively format plural and select parts' option — which can be a
        // nested pattern structure. The choosing of the option to use is
        // abstracted-by and delegated-to the part helper object.
        if (isDateElement(el)) {
            const style = typeof el.style === 'string'
                ? formats.date[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isTimeElement(el)) {
            const style = typeof el.style === 'string'
                ? formats.time[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isNumberElement(el)) {
            const style = typeof el.style === 'string'
                ? formats.number[el.style]
                : isNumberSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            if (style && style.scale) {
                value =
                    value *
                        (style.scale || 1);
            }
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getNumberFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        // AMAZON_CHANGE_START
        if (isPriceElement(el)) {
            const style = typeof el.style === 'string' ? formats.price[el.style] : undefined;
            const formattedPrice = formatters
                .getPriceFormat(localizationContext || locales, style)
                .format(value, style);
            result.push({
                type: typeof formattedPrice === 'string'
                    ? PART_TYPE.literal
                    : PART_TYPE.object,
                value: formattedPrice,
            });
            continue;
        }
        // AMAZON_CHANGE_END
        if (isSelectElement(el)) {
            const opt = el.options[value] || el.options.other;
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            result.push(...formatToParts(opt.value, 
            // AMAZON_CHANGE_START
            localesOrLocalizationContext, 
            // AMAZON_CHANGE_END
            formatters, formats, values, currentPluralValue, originalMessage, hyperscriptRuntime));
            continue;
        }
        if (isPluralElement(el)) {
            let opt = el.options[`=${value}`];
            if (!opt) {
                if (!Intl.PluralRules) {
                    throw new FormatError(`Intl.PluralRules is not available in this environment.
Try polyfilling it using "@formatjs/intl-pluralrules"
`, ErrorCode.MISSING_INTL_API, originalMessage);
                }
                const rule = formatters
                    .getPluralRules(locales, { type: el.pluralType })
                    .select(value - (el.offset || 0));
                opt = el.options[rule] || el.options.other;
            }
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            result.push(...formatToParts(opt.value, 
            // AMAZON_CHANGE_START
            localesOrLocalizationContext, 
            // AMAZON_CHANGE_END
            formatters, formats, values, value - (el.offset || 0), originalMessage, hyperscriptRuntime));
            continue;
        }
    }
    // AMAZON_CHANGE_START
    // Return individual formatted VDOM parts.
    if (hyperscriptRuntime) {
        return result;
    }
    // AMAZON_CHANGE_END
    return mergeLiteral(result);
}
// AMAZON_CHANGE_END

const UNDEFINED_LOCALE = 'und';
// Map of deprecated/unsupported language tags pointing to their modern/supported counterparts.
const SPECIAL_CASE_LANGUAGES = new Map([
    ["no", "nb"],
    ["iw", "he"],
    ["in", "id"],
    ["ji", "yi"],
]);
/**
 * Processes locale string to be accepted by NumberFormat.
 * @param {string} [locale] Locale string (e.g. "zh-Hans-CN")
 */
function processLocale(locale) {
    // If locale is empty, null it will throw an error in NumberFormat.
    // Therefore if locale is falsy we return the default locale of the system.
    if (!locale) {
        return new Intl.NumberFormat().resolvedOptions().locale;
    }
    // Check whether the locale is supported.
    const supportedLocales = Intl.NumberFormat.supportedLocalesOf(locale);
    // If locale is not supported return undefined locale.
    if (supportedLocales.length === 0) {
        // @ts-ignore
        const intlLocale = new Intl.Locale(locale);
        // Check for exempted locales.
        const specialLanguage = SPECIAL_CASE_LANGUAGES.get(intlLocale.language);
        if (specialLanguage) {
            // @ts-ignore
            const tmp = new Intl.Locale(intlLocale.baseName.replace(intlLocale.language, specialLanguage));
            return tmp.baseName;
        }
        console.log(`Unsupported locale: ${locale}`);
        return UNDEFINED_LOCALE;
    }
    return supportedLocales[0];
}

/**
 * Custom Symbol Overrides that are not locale specific.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$j = {
    sek_a2nodrkzp88zb9: {
        customSymbol: 'kr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sek_at614yyynoc1s: {
        customSymbol: 'kr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    pln_a1c3sozrarq6r3: {
        customSymbol: '\u007a\u0142',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    pln_aky4k4wkh21yq: {
        customSymbol: '\u007a\u0142',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sgd: {
        customSymbol: 'S$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    aed_a2vigq35rcs4ug: {
        customSymbol: 'AED',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    aed_a34gyyczvdbsik: {
        customSymbol: 'AED',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    sar_a17e79c6d8dwnp: {
        customSymbol: 'SAR',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    sar_a1mqpsgj6u9q54: {
        customSymbol: 'SAR',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    egp_arbp9ooshtchu: {
        customSymbol: 'EGP',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    egp_aujpm9xgfjrc7: {
        customSymbol: 'EGP',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    try_a33avaj2pdy3ev: {
        customSymbol: 'TL',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    try_a3cqbqd3rgpjr8: {
        customSymbol: 'TL',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$j = {};
const SYMBOL_POSITION_OVERRIDES$j = {};
const DEFAULT_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$j,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$j,
    numberShape: NUMBER_SHAPE_OVERRIDES$j
};

/**
 * Custom Symbol Overrides for the 'ar' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$i = {
    aed_a2vigq35rcs4ug: {
        customSymbol: '\u062f\u0631\u0647\u0645',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    aed_a34gyyczvdbsik: {
        customSymbol: '\u062f\u0631\u0647\u0645',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    sar_a17e79c6d8dwnp: {
        customSymbol: '\u0631\u064a\u0627\u0644',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    sar_a1mqpsgj6u9q54: {
        customSymbol: '\u0631\u064a\u0627\u0644',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    egp_arbp9ooshtchu: {
        customSymbol: '\u062c\u0646\u064a\u0647',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    egp_aujpm9xgfjrc7: {
        customSymbol: '\u062c\u0646\u064a\u0647',
        insertSpaceInPattern: true,
        applySentenceOverride: false
    },
    /*
      This override addresses the issue that appears when rendering the USD currency symbol in the ar
      locale (US$) for negative numbers in US marketplace(https://issues.amazon.com/I18NFND-55).
 
      Safari browsers do not yet support bidi isolates such as FSI (U+2068) and PDI (U+2069) so an ALM
      (U+061C) is placed at the beginning of the currency symbol as a workaround for the time-being.
    */
    usd_atvpdkikx0der: {
        customSymbol: '\u061CUS$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$i = {};
const SYMBOL_POSITION_OVERRIDES$i = {
    symbol_position: "end" /* End */
};
const NON_MARKETPLACE_DEFAULT_CURRENCY_OVERRIDES$1 = {
    /*
        Add ALM as the prefix for non-marketplace default currencies. This ensures that
        we apply right-to-left ordering for the currency symbol (ISO code) relative to the
        rest of the formatted price string. This in particular becomes relevant for negative
        values, when the minus sign is prefixed with LRM from CLDR (https://issues.amazon.com/I18NFND-55).
     */
    symbolPrefix: '\u061C'
};
const AR_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$i,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$i,
    numberShape: NUMBER_SHAPE_OVERRIDES$i,
    nonMarketplaceDefaultCurrencyOverrides: NON_MARKETPLACE_DEFAULT_CURRENCY_OVERRIDES$1
};

/**
 * Custom Symbol Overrides for the 'bn' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$h = {};
const NUMBER_SHAPE_OVERRIDES$h = {};
const SYMBOL_POSITION_OVERRIDES$h = {
    symbol_position: "start" /* Start */
};
const BN_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$h,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$h,
    numberShape: NUMBER_SHAPE_OVERRIDES$h
};

/**
 /**
 * Custom Symbol Overrides for the 'da' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$g = {
    sek_a2nodrkzp88zb9: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sek_at614yyynoc1s: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$g = {};
const SYMBOL_POSITION_OVERRIDES$g = {};
const DA_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$g,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$g,
    numberShape: NUMBER_SHAPE_OVERRIDES$g
};

/**
 * Custom Symbol Overrides for the 'et' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$f = {
    sek_a2nodrkzp88zb9: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sek_at614yyynoc1s: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$f = {};
const SYMBOL_POSITION_OVERRIDES$f = {};
const ET_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$f,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$f,
    numberShape: NUMBER_SHAPE_OVERRIDES$f
};

/**
 * Custom Symbol Overrides for the pseudo locale 'en-XD' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$e = {};
const NUMBER_SHAPE_OVERRIDES$e = {
    number_shape: 'latn'
};
const SYMBOL_POSITION_OVERRIDES$e = {};
const EN_XD_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$e,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$e,
    numberShape: NUMBER_SHAPE_OVERRIDES$e
};

/**
 * Custom Symbol Overrides for the pseudo locale 'en-Phnx-XD-unifon' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$d = {};
const NUMBER_SHAPE_OVERRIDES$d = {
    number_shape: 'ahom'
};
const SYMBOL_POSITION_OVERRIDES$d = {};
const EN_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$d,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$d,
    numberShape: NUMBER_SHAPE_OVERRIDES$d
};

/**
 * Custom Symbol Overrides for the 'es' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$c = {
    usd_atvpdkikx0der_compact: {
        customSymbol: '$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    usd_atvpdkikx0der: {
        customSymbol: 'US$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$c = {};
const SYMBOL_POSITION_OVERRIDES$c = {};
const ES_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$c,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$c,
    numberShape: NUMBER_SHAPE_OVERRIDES$c
};

/**
 * Custom Symbol Overrides for the 'fr-CA' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$b = {
    cad_a2euq1wtgctbg2: {
        customSymbol: '$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    cad_a1nwp2e4ekqp94: {
        customSymbol: '$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    cad_a2vji655wt2i0b: {
        customSymbol: '$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    cad_asbg27dkm7ex6: {
        customSymbol: '$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    cad_a2ce4mdcuaasdp: {
        customSymbol: '$',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$b = {};
const SYMBOL_POSITION_OVERRIDES$b = {};
const FR_CA_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$b,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$b,
    numberShape: NUMBER_SHAPE_OVERRIDES$b
};

/**
 * Custom Symbol Overrides for the pseudo locale 'fr-XD' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$a = {};
const NUMBER_SHAPE_OVERRIDES$a = {
    number_shape: 'deva'
};
const SYMBOL_POSITION_OVERRIDES$a = {
    symbol_position: "end" /* End */
};
const FR_XD_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$a,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$a,
    numberShape: NUMBER_SHAPE_OVERRIDES$a
};

/**
 * Custom Symbol Overrides for the pseudo locale 'fr-XD-unifon' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$9 = {};
const NUMBER_SHAPE_OVERRIDES$9 = {};
const SYMBOL_POSITION_OVERRIDES$9 = {};
const FR_XD_UNIFON_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$9,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$9,
    numberShape: NUMBER_SHAPE_OVERRIDES$9
};

/**
 * Custom Symbol Overrides for the pseudo locale 'fr-Phnx-XD-unifon' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$8 = {};
const NUMBER_SHAPE_OVERRIDES$8 = {
    number_shape: 'latn'
};
const SYMBOL_POSITION_OVERRIDES$8 = {
    symbol_position: "start" /* Start */
};
const FR_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$8,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$8,
    numberShape: NUMBER_SHAPE_OVERRIDES$8
};

/**
 * Custom Symbol Overrides for 'he' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$7 = {};
const NUMBER_SHAPE_OVERRIDES$7 = {};
const SYMBOL_POSITION_OVERRIDES$7 = {};
const NON_MARKETPLACE_DEFAULT_CURRENCY_OVERRIDES = {
    /*
        Add RLM as the prefix for non-marketplace default currencies. This ensures that
        we apply right-to-left ordering for the currency symbol (ISO code) relative to the
        rest of the formatted price string. This in particular becomes relevant for negative
        values, when the minus sign is prefixed with LRM from CLDR (https://issues.amazon.com/I18NFND-55).
     */
    symbolPrefix: '\u200F'
};
const HE_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$7,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$7,
    numberShape: NUMBER_SHAPE_OVERRIDES$7,
    nonMarketplaceDefaultCurrencyOverrides: NON_MARKETPLACE_DEFAULT_CURRENCY_OVERRIDES
};

/**
 * Custom Symbol Overrides for the 'is' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$6 = {
    sek_a2nodrkzp88zb9: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sek_at614yyynoc1s: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$6 = {};
const SYMBOL_POSITION_OVERRIDES$6 = {};
const IS_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$6,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$6,
    numberShape: NUMBER_SHAPE_OVERRIDES$6
};

/**
 * Custom Symbol Overrides for the 'ja' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$5 = {
    jpy_insentence: {
        customSymbol: '\u5186',
        insertSpaceInPattern: false,
        applySentenceOverride: true
    },
    jpy_a1vc38t7yxb528_compact: {
        customSymbol: '\u00A5',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$5 = {};
const SYMBOL_POSITION_OVERRIDES$5 = {};
const JA_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$5,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$5,
    numberShape: NUMBER_SHAPE_OVERRIDES$5
};

/**
 * Custom Symbol Overrides for the 'nb' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$4 = {
    sek_a2nodrkzp88zb9: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sek_at614yyynoc1s: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$4 = {};
const SYMBOL_POSITION_OVERRIDES$4 = {};
const NB_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$4,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$4,
    numberShape: NUMBER_SHAPE_OVERRIDES$4
};

/**
 * Custom Symbol Overrides for the 'nn' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$3 = {
    sek_a2nodrkzp88zb9: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sek_at614yyynoc1s: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$3 = {};
const SYMBOL_POSITION_OVERRIDES$3 = {};
const NN_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$3,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$3,
    numberShape: NUMBER_SHAPE_OVERRIDES$3
};

/**
 * Custom Symbol Overrides for the 'no' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$2 = {
    sek_a2nodrkzp88zb9: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    },
    sek_at614yyynoc1s: {
        customSymbol: 'Skr',
        insertSpaceInPattern: false,
        applySentenceOverride: false
    }
};
const NUMBER_SHAPE_OVERRIDES$2 = {};
const SYMBOL_POSITION_OVERRIDES$2 = {};
const NO_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$2,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$2,
    numberShape: NUMBER_SHAPE_OVERRIDES$2
};

/**
 * Custom Symbol Overrides for the 'tr' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$1 = {};
const NUMBER_SHAPE_OVERRIDES$1 = {};
// Remove this override when the symbol placement on end has been made default in CLDR:
// https://unicode-org.atlassian.net/browse/CLDR-14070
const SYMBOL_POSITION_OVERRIDES$1 = {
    symbol_position: "end" /* End */
};
const TR_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$1,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$1,
    numberShape: NUMBER_SHAPE_OVERRIDES$1
};

/**
 * Custom Symbol Overrides for the 'zh' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES = {
    cny_insentence: {
        customSymbol: '\u5143',
        insertSpaceInPattern: false,
        applySentenceOverride: true
    },
    jpy_insentence: {
        customSymbol: '\u65E5\u5143',
        insertSpaceInPattern: false,
        applySentenceOverride: true
    }
};
const NUMBER_SHAPE_OVERRIDES = {};
const SYMBOL_POSITION_OVERRIDES = {};
const ZH_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES,
    numberShape: NUMBER_SHAPE_OVERRIDES
};

const DEFAULT_KEY = 'default';
const FIELD_ORDER_SEPARATOR = '_';
const NUMBER_SHAPE = 'number_shape';
const SYMBOL_POSITION = 'symbol_position';
const NON_MARKETPLACE_DEFAULT_CURRENCY_OVERRIDES_KEY = 'nonMarketplaceDefaultCurrencyOverrides';
const LOCALE_SEPARATOR = '-';
const LOCALE_SEPARATOR_REGEX = new RegExp(LOCALE_SEPARATOR, 'g');
const DEFAULT_NUMBERING_SYSTEM = 'latn';
// Map containing the number shapes and custom symbol overrides by language, when a new
// custom-symbol-overrides-*.ts file is added it should be added to this map.
const PRICE_FORMAT_OVERRIDES_MAP = {
    ar: AR_PRICE_FORMAT_OVERRIDES,
    bn: BN_PRICE_FORMAT_OVERRIDES,
    da: DA_PRICE_FORMAT_OVERRIDES,
    et: ET_PRICE_FORMAT_OVERRIDES,
    es: ES_PRICE_FORMAT_OVERRIDES,
    fr_ca: FR_CA_PRICE_FORMAT_OVERRIDES,
    he: HE_PRICE_FORMAT_OVERRIDES,
    is: IS_PRICE_FORMAT_OVERRIDES,
    ja: JA_PRICE_FORMAT_OVERRIDES,
    nb: NB_PRICE_FORMAT_OVERRIDES,
    nn: NN_PRICE_FORMAT_OVERRIDES,
    no: NO_PRICE_FORMAT_OVERRIDES,
    tr: TR_PRICE_FORMAT_OVERRIDES,
    zh: ZH_PRICE_FORMAT_OVERRIDES,
    // Test locales
    en_xd: EN_XD_PRICE_FORMAT_OVERRIDES,
    en_phnx_xd_unifon: EN_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES,
    fr_xd: FR_XD_PRICE_FORMAT_OVERRIDES,
    fr_xd_unifon: FR_XD_UNIFON_PRICE_FORMAT_OVERRIDES,
    fr_phnx_xd_unifon: FR_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES,
    [DEFAULT_KEY]: DEFAULT_PRICE_FORMAT_OVERRIDES
};
/**
 * This class is based on the Java ResourceBundle which allows for language
 * specific overrides. It implements methods to get {@link CustomSymbolData} and
 * a LocaleCustomNumberFormat.
 */
class CustomSymbolOverrides {
    /**
     * Given a locale create a list of available ResourceBundles which have locale specific
     * overrides or the default overrides.
     * @param {string} locale
     */
    constructor(locale) {
        this.overrides = [];
        this.locale = processLocale(locale);
        let intlLocale;
        try {
            // @ts-ignore
            intlLocale = new Intl.Locale(this.locale);
        }
        catch (e) {
            console.log(`Failed creating Intl.Locale object - ${e}. Unsupported locale: ${locale}`);
        }
        // Generate fallback chain of override configs similar to Java ResourceBundle:
        // https://docs.oracle.com/javase/8/docs/api/java/util/ResourceBundle.html#getBundle-java.lang.String-java.util.Locale-java.lang.ClassLoader-
        const overrideKeys = [];
        // loop the complete base name
        let loc = intlLocale.baseName;
        loc = loc.replace(LOCALE_SEPARATOR_REGEX, FIELD_ORDER_SEPARATOR);
        let pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
        while (pos > 0) {
            overrideKeys.push(loc.toLocaleLowerCase('und'));
            loc = loc.substring(0, pos);
            pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
        }
        // if script exists, remove it and re-loop
        if (intlLocale.script) {
            // @ts-ignore
            const tmp = new Intl.Locale(intlLocale.baseName.replace(LOCALE_SEPARATOR + intlLocale.script, ''));
            loc = tmp.baseName;
            loc = loc.replace(LOCALE_SEPARATOR_REGEX, FIELD_ORDER_SEPARATOR);
            pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
            while (pos > 0) {
                overrideKeys.push(loc.toLocaleLowerCase('und'));
                loc = loc.substring(0, pos);
                pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
            }
        }
        // capture the primary language subtag
        overrideKeys.push(loc.toLocaleLowerCase('und'));
        for (const key of overrideKeys) {
            const priceFormatOverride = PRICE_FORMAT_OVERRIDES_MAP[key];
            if (priceFormatOverride) {
                this.overrides.push(priceFormatOverride);
            }
        }
        this.overrides.push(PRICE_FORMAT_OVERRIDES_MAP[DEFAULT_KEY]);
    }
    /**
     * Get the custom symbol data for currency code. Checks the overrides in the following manner:
     * Custom Symbol Overrides takes precedence over locale specific overrides and contains key formats and
     * hierarchy for symbol override:
     *                           [currencyCode]_[obfuscatedMarketplaceId]_[type]
     *                           [currencyCode]_[obfuscatedMarketplaceId]
     *                           [currencyCode]_[type]
     *                           [currencyCode]
     * value - CustomSymbolData Object
     *         arg1: customSymbol
     *                - custom symbol to be used as override
     *         arg2: insertSpaceInPattern
     *                - optional argument, set to false by default. Boolean to specify whether a SPACE is required between
     *                  the symbol and price in case the required format is not provided by the default marketplace format.
     *
     * @param {string} currencyCode - The 3 letter ISO 4217 currency code.
     * @param {string} typeName - The type for formatting price.
     * @param {string} marketplaceId - The obfuscated marketplace id.
     * @return {CustomSymbolData | undefined} The CustomSymbolData object if present, else undefined.
     */
    getCustomSymbolData(currencyCode, typeName, marketplaceId) {
        if (!currencyCode) {
            throw new Error('Currency Code is falsy.');
        }
        if (marketplaceId) {
            marketplaceId = marketplaceId.toLowerCase();
        }
        if (typeName) {
            typeName = typeName.toLowerCase();
        }
        const currency = currencyCode.toLowerCase();
        for (const override of this.overrides) {
            const resourceKey = this.customSymbolDataContainsKey(getResourceKey(currency, marketplaceId, typeName), override)
                ? getResourceKey(currency, marketplaceId, typeName)
                : this.customSymbolDataContainsKey(getResourceKey(currency, marketplaceId), override)
                    ? getResourceKey(currency, marketplaceId)
                    : this.customSymbolDataContainsKey(getResourceKey(currency, typeName), override)
                        ? getResourceKey(currency, typeName)
                        : this.customSymbolDataContainsKey(getResourceKey(currency), override)
                            ? getResourceKey(currency)
                            : undefined;
            if (resourceKey) {
                return override.customSymbolData[resourceKey];
            }
        }
        return undefined;
    }
    /**
     * Get a locale with a set numbering system. If the input locale already has a number system set (i.e. -u-nu- locale extension),
     * the locale is returned as is. Otherwise, the locale's numbering system is set to the number shape override if present,
     * else defaults to Latin.
     *
     * @return {string} Locale with a set numbering system.
     */
    getLocaleCustomNumberFormat() {
        // @ts-ignore
        let intlLocale = new Intl.Locale(this.locale);
        if (intlLocale.numberingSystem) {
            // If a numbering system is already present, we do not change the locale string.
            return this.locale;
        }
        // If a number shape override is present, we set it as the numbering system on the locale string, else
        // set the default numbering system as Latin (latn).
        let desiredNumberShape = DEFAULT_NUMBERING_SYSTEM;
        for (const override of this.overrides) {
            if (this.numberShapeContainsKey(NUMBER_SHAPE, override)) {
                desiredNumberShape = override.numberShape[NUMBER_SHAPE];
                break;
            }
        }
        // @ts-ignore
        intlLocale = new Intl.Locale(this.locale, { numberingSystem: desiredNumberShape });
        return intlLocale.toLocaleString();
    }
    /**
     * Get overrides applicable for non-marketplace default currencies.
     *
     * @return {NonMarketplaceDefaultCurrencyOverrides|undefined} NonMarketplaceDefaultCurrencyOverrides instance if present, undefined
     * otherwise.
     */
    getNonMarketplaceDefaultCurrencyOverrides() {
        for (const override of this.overrides) {
            if (this.containsKey(NON_MARKETPLACE_DEFAULT_CURRENCY_OVERRIDES_KEY, override)) {
                return override.nonMarketplaceDefaultCurrencyOverrides;
            }
        }
        return undefined;
    }
    /**
     * Get custom symbol position for overriding the position for symbol.
     * If no overrides are present, then returns Default.
     * @return {CurrencySymbolPosition} symbol position for overriding currency symbol position
     */
    getSymbolPositionOverride() {
        for (const override of this.overrides) {
            if (this.symbolPositionContainsKey(SYMBOL_POSITION, override)) {
                return override.symbolPosition[SYMBOL_POSITION];
            }
        }
        return "default" /* Default */;
    }
    customSymbolDataContainsKey(key, override) {
        return this.containsKey(key, override.customSymbolData);
    }
    numberShapeContainsKey(key, override) {
        return this.containsKey(key, override.numberShape);
    }
    symbolPositionContainsKey(key, override) {
        return this.containsKey(key, override.symbolPosition);
    }
    /**
     * Check whether the overrides contain a particular key.
     * @param {string} key Overrides key
     * @param {Object.<string, any>} overrides Overrides
     */
    containsKey(key, overrides) {
        if (!overrides) {
            return false;
        }
        return overrides.hasOwnProperty(key);
    }
}
/**
 * Constructs key for resource retrieval from the CustomSymbolOverrides based on order of key field inputs.
 *
 * @param {ReadonlyArray<string>} keyFields - The fields used to construct the resource key.
 * @return {string} The key string used to fetch the relevant resource from the CustomSymbolOverrides.
 */
function getResourceKey(...keyfields) {
    if (!keyfields || keyfields.length === 0) {
        throw new Error('The key field(s) must not be null or empty');
    }
    return keyfields.join(FIELD_ORDER_SEPARATOR);
}

const CURRENCY_STYLE = 'currency';
const CURRENCY_TYPE = 'currency';
const LITERAL_TYPE = 'literal';
const MINUS_SIGN_TYPE = 'minusSign';
const FRACTION_TYPE = 'fraction';
const DECIMAL_TYPE = 'decimal';
const INTEGER_TYPE = 'integer';
const GROUP_TYPE = 'group';
const NO_BREAK_SPACE = '\u00A0';
const SPECIAL_CHARS_REGEX = new RegExp('[\u200e\u200f\u202a-\u202e\u2066-\u2069\u061c]+');
const NUMBER_FORMAT_LRU_CACHE_SIZE = typeof process !== 'undefined' && process.env.I18N_PRICE_FORMAT_NUMBER_FORMAT_CACHE_SIZE
    ? parseInt(process.env.I18N_PRICE_FORMAT_NUMBER_FORMAT_CACHE_SIZE, 10)
    : 1000;
/**
 * {@link PriceFormat} is a class for formatting price.
 *
 * This file provides a set of factory methods for obtaining the formatters based on the given locale,
 * a formatting type, currency, .... The formatting types are Iso, Compact, Plural, InSentence and AmazonSymbol.
 * More details and sample examples of using these formatting types are provided in the {@link Type}.
 *
 * If you are formatting multiple prices, it is more efficient to get the PriceFormat and use it multiple times
 * so that the system doesn't have to fetch the information about the local language and country conventions multiple
 * times.
 *
 * Create global marketplace settings by 'MarketplaceCreator.setProvider(<MarketplaceProvider object>)'
 * If {@link MarketplaceCreator} set with custom provider, marketplace is generated by the specified provider.
 * Otherwise, {@link EmptyMarketplaceProvider} with undefined marketplaceId, locale and currencyCode is used by default.
 *
 * In application, passing marketplace setting through getInstance methods is supported.
 * The marketplaceId or marketplace object overrides the marketplace provider's specified settings.
 */
class PriceFormat {
    /**
     * Creates a PriceFormat class.
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @param {Marketplace} marketplace - The marketplace object
     */
    constructor(locale, type, marketplace) {
        this.numberFormatCache = new QuickLRU$1({ maxSize: NUMBER_FORMAT_LRU_CACHE_SIZE });
        marketplace = marketplace || MarketplaceCreator.provider().getMarketplace();
        this.marketplaceDefaultCurrencyCode = marketplace.getDefaultCurrency();
        this.customSymbolOverrides = new CustomSymbolOverrides(locale);
        this.locale = this.customSymbolOverrides.getLocaleCustomNumberFormat();
        // Default to AmazonSymbol if no type specified
        this.type = type ? type : "amazonSymbol" /* AmazonSymbol */;
        this.marketplaceId = marketplace.getObfuscatedMarketplaceId();
    }
    /**
     * Get number format result of price
     * @param {Price} price - The price to be formatted
     * @return {string} number format result in string format
     */
    format(price) {
        return getDisplayString(this.getNumberFormatParts(price));
    }
    /**
     * Get number format result of price
     * @param {Price} price - The price to be formatted
     * @return {I18NPriceFormatParts} number format result in the form of I18NPriceFormatParts
     */
    formatToParts(price) {
        const numberFormatParts = this.getNumberFormatParts(price);
        const fieldOrder = determineFieldOrder(numberFormatParts, this.type);
        const integerValue = getNumberFormatPartValue(numberFormatParts, INTEGER_TYPE);
        const currencySymbol = getNumberFormatPartValue(numberFormatParts, CURRENCY_TYPE);
        const decimalSeparator = getNumberFormatPartValue(numberFormatParts, DECIMAL_TYPE);
        const fractionalValue = getNumberFormatPartValue(numberFormatParts, FRACTION_TYPE);
        if (!integerValue || !currencySymbol) {
            throw new Error('Currency symbol or integer value are not present');
        }
        const displayFields = {
            fieldOrder,
            integerValue,
            currencySymbol,
            decimalSeparator,
            fractionalValue
        };
        return {
            amount: price.amount,
            currency: price.currency,
            displayString: getDisplayString(numberFormatParts),
            displayFields
        };
    }
    /**
     * Returns a NumberFormat object configured for the Currency, Type and Locale passed in.
     * @param {Currency} currency - The requested currency
     * @return {AmazonNumberFormat} The Amazon number format.
     */
    getFormat(currency) {
        // boolean flag to determine if the custom symbol overrides occur in this format.
        let symbolOverrideApplied = false;
        let applySentenceOverride = false;
        let insertSpaceInPattern = false;
        let currencySymbol;
        let symbolPosition = "default" /* Default */;
        if (shouldApplyCustomSymbol(this.type)) {
            const customSymbolData = this.customSymbolOverrides.getCustomSymbolData(currency.currencyCode, this.type.toString(), this.marketplaceId || '');
            if (this.marketplaceDefaultCurrencyCode &&
                !(this.marketplaceDefaultCurrencyCode === currency.currencyCode) &&
                (!customSymbolData || !customSymbolData.customSymbol)) {
                symbolOverrideApplied = true;
                currencySymbol = this.getNonMarketplaceDefaultCurrencySymbol(currency.currencyCode);
                /* Insert space between currency symbol and amount, since we are replacing
                   with ISO code.*/
                insertSpaceInPattern = true;
            }
            else if (customSymbolData && customSymbolData.customSymbol) {
                currencySymbol = customSymbolData.customSymbol;
                symbolOverrideApplied = true;
                insertSpaceInPattern = customSymbolData.insertSpaceInPattern;
                applySentenceOverride = customSymbolData.applySentenceOverride;
            }
        }
        if (shouldApplySymbolPositionOverride(this.type)) {
            const applySymbolPositionOverride = this.customSymbolOverrides.getSymbolPositionOverride();
            symbolPosition = applySentenceOverride ? "end" /* End */ : applySymbolPositionOverride;
        }
        return {
            numberFormat: this.getNumberFormatFromCacheOrCreate(this.locale, currency, this.type),
            currencySymbol,
            insertSpaceInPattern,
            symbolOverrideApplied,
            symbolPosition
        };
    }
    /**
     * Get Intl.NumberFormatPart result of price
     * @param {Price} price - The price to be formatted
     * @return {Intl.NumberFormatPart[]} Array of number format components
     */
    getNumberFormatParts(price) {
        const amount = price.amount;
        const currency = price.currency;
        const amazonNumberFormat = this.getFormat(currency);
        // Check whether amount is a number for consumers without un-type-checked content
        if (typeof amount !== 'number' || Number.isNaN(amount)) {
            throw new Error('Amount is not a number');
        }
        const roundedAmount = roundAmount(amount, this.type);
        let numberFormatParts = amazonNumberFormat.numberFormat.formatToParts(roundedAmount);
        if (this.type === "compact" /* Compact */) {
            numberFormatParts = removeSpaceBetweenSymbolAndAmount(numberFormatParts);
        }
        else if (amazonNumberFormat.insertSpaceInPattern) {
            numberFormatParts = insertSpaceInFormat(numberFormatParts);
        }
        if (amazonNumberFormat.symbolPosition !== "default" /* Default */) {
            numberFormatParts = updateFormatWithSymbolPosition(this.type, numberFormatParts, amazonNumberFormat.symbolPosition);
        }
        if (!numberFormatParts) {
            throw new Error('Intl.NumberFormat formatToParts returned empty');
        }
        return numberFormatParts.map(({ type, value }) => {
            switch (type) {
                case CURRENCY_TYPE:
                    if (amazonNumberFormat.symbolOverrideApplied && amazonNumberFormat.currencySymbol) {
                        value = amazonNumberFormat.currencySymbol;
                    }
                    return { type, value };
                default:
                    return { type, value };
            }
        });
    }
    /**
     * Get currency symbol with non-marketplace default currency overrides applied.
     * @param {string} currencyCode - Currency code.
     * @return {string} Currency symbol with overrides applied if currencyCode is defined, otherwise return undefined.
     */
    getNonMarketplaceDefaultCurrencySymbol(currencyCode) {
        if (!currencyCode) {
            return undefined;
        }
        const nonMarketplaceDefaultCurrencyOverrides = this.customSymbolOverrides.getNonMarketplaceDefaultCurrencyOverrides();
        const symbolPrefix = nonMarketplaceDefaultCurrencyOverrides
            ? nonMarketplaceDefaultCurrencyOverrides.symbolPrefix
            : undefined;
        return symbolPrefix ? symbolPrefix + currencyCode : currencyCode;
    }
    getNumberFormatFromCacheOrCreate(locale, currency, type) {
        const key = this.serializeNumberFormatCacheKey(locale, currency, type);
        let numberFormat = this.numberFormatCache.get(key);
        if (numberFormat === undefined) {
            numberFormat = new Intl.NumberFormat(this.locale, createCurrencyNumberFormatOptions(currency, type));
            this.numberFormatCache.set(key, numberFormat);
        }
        return numberFormat;
    }
    serializeNumberFormatCacheKey(locale, currency, type) {
        return `${locale}-${currency.currencyCode}-${type}`;
    }
    /**
     * Get a price formatter for a given locale.
     * This will use the default Type (Type.AmazonSymbol) and
     * marketplace provider's specified settings to define the default marketplace for formatting.
     * @param {string} locale - The requested locale
     * @return {PriceFormat} a price formatter
     */
    static getInstance(locale) {
        return new PriceFormat(locale, "amazonSymbol" /* AmazonSymbol */);
    }
    /**
     * Get a price formatter for a given locale and type.
     * This will use marketplace provider's specified settings to define the default marketplace for formatting
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByType(locale, type) {
        return new PriceFormat(locale, type);
    }
    /**
     * Get a price formatter for a given locale and marketplace id.
     * This will use the default Type (Type.AmazonSymbol) for formatting.
     * @param {string} locale - The requested locale
     * @param {string} marketplaceId - The marketplace id
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByMarketplaceId(locale, marketplaceId) {
        return new PriceFormat(locale, "amazonSymbol" /* AmazonSymbol */, new DefaultMarketplaceImpl(marketplaceId));
    }
    /**
     * Get a price formatter for a given locale and marketplace object.
     * This will use the default Type (Type.AmazonSymbol) for formatting.
     * @param {string} locale - The requested locale
     * @param {Marketplace} marketplace - The marketplace object
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByMarketplace(locale, marketplace) {
        return new PriceFormat(locale, "amazonSymbol" /* AmazonSymbol */, marketplace);
    }
    /**
     * Get a price formatter for a given locale, type and marketplace id.
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @param {string} marketplaceId - The marketplace id
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByTypeAndMarketplaceId(locale, type, marketplaceId) {
        return new PriceFormat(locale, type, new DefaultMarketplaceImpl(marketplaceId));
    }
    /**
     * Get a price formatter for a given locale, type and marketplace object.
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @param {Marketplace} marketplace - The marketplace object
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByTypeAndMarketplace(locale, type, marketplace) {
        return new PriceFormat(locale, type, marketplace);
    }
}
/**
 * Given array of number format components returns the displayable string
 * @param {Intl.NumberFormatPart[]} numberFormatParts Array of number format components
 */
function getDisplayString(numberFormatParts) {
    return numberFormatParts.map(({ value }) => value).reduce((string, part) => string + part);
}
/**
 * Given an array of number format components get the value of the given type
 * @param {Intl.NumberFormatPart[]} numberFormatParts Array of number format components
 * @param {Intl.NumberFormatPartTypes} numberFormatPartType Requested number format type
 */
function getNumberFormatPartValue(numberFormatParts, numberFormatPartType) {
    let value = '';
    for (const numberFormatPart of numberFormatParts) {
        if (numberFormatPartType === INTEGER_TYPE &&
            (numberFormatPart.type === INTEGER_TYPE || numberFormatPart.type === GROUP_TYPE)) {
            value += numberFormatPart.value;
        }
        else if (numberFormatPart.type === numberFormatPartType) {
            return numberFormatPart.value;
        }
    }
    return value ? value : undefined;
}
/**
 * Given a number format parts array find the field order of the to be displayed price
 * @param {Intl.NumberFormatPart[]} numberFormatParts Array of number format components
 * @param {Type} type Requested price format type
 */
function determineFieldOrder(numberFormatParts, type) {
    if (!hasFraction(numberFormatParts) || type === "compact" /* Compact */) {
        return !isSymbolLast(numberFormatParts) ? 2 /* SYMBOL_NUMBER */ : 5 /* NUMBER_SYMBOL */;
    }
    if (!isSymbolLast(numberFormatParts)) {
        return hasDecimalSeparator(numberFormatParts)
            ? 0 /* SYMBOL_NUMBER_SEPARATOR_DECIMAL */
            : 1 /* SYMBOL_NUMBER_DECIMAL */;
    }
    else {
        return hasDecimalSeparator(numberFormatParts)
            ? 3 /* NUMBER_SEPARATOR_DECIMAL_SYMBOL */
            : 4 /* NUMBER_DECIMAL_SYMBOL */;
    }
}
/**
 * Update the pattern if necessary to have the currency symbol at the desired position.
 *
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @param {Type} type - The requested formatting type
 * @param {CurrencySymbolPosition} symbolPosition - Custom Symbol position for override
 * @return {Intl.NumberFormatPart[]} Array containing the format components in the updated order
 */
function updateFormatWithSymbolPosition(type, formatParts, symbolPosition) {
    if (symbolPosition === "end" /* End */ && !isSymbolLast(formatParts)) {
        // If space is already present in the pattern, move it to the end of the format
        formatParts = updateSpacePositionInFormatParts(type, formatParts, symbolPosition, formatParts.length - 1, false);
        const symbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, false);
        // Move currency symbol to the end of the price format
        formatParts = moveArrayElement(formatParts, symbolIndex, formatParts.length - 1);
    }
    else if (symbolPosition === "start" /* Start */ && isSymbolLast(formatParts)) {
        let newSymbolIndex = 0;
        // In case there is a literal in starting.
        if (formatParts[newSymbolIndex].type === LITERAL_TYPE) {
            newSymbolIndex++;
        }
        // In case the number is negative.
        if (formatParts[newSymbolIndex].type === MINUS_SIGN_TYPE) {
            newSymbolIndex++;
        }
        // If space is already present in the pattern, move it to the start of the format
        formatParts = updateSpacePositionInFormatParts(type, formatParts, symbolPosition, newSymbolIndex, true);
        const currentSymbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, true);
        // Move currency symbol to the start of the price format
        formatParts = moveArrayElement(formatParts, currentSymbolIndex, newSymbolIndex);
    }
    return formatParts;
}
/**
 * Update the pattern with correct space position (if found) as per the symbol position -
 *      1. it will be appended in end when custom symbol position is at end
 *      2. it will be appended in start when custom symbol position is at start
 * In case type is COMPACT space will be removed.
 *
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @param {Type} type - The requested formatting type
 * @param {CurrencySymbolPosition} symbolPosition - Custom Symbol position for override
 * @param {number} newSpaceIndex - Index for updating the space position
 * @param {boolean} isSymbolLast - True in case symbol position is last false otherwise
 * @return {Intl.NumberFormatPart[]} Array containing the format components in the updated order
 */
function updateSpacePositionInFormatParts(type, formatParts, symbolPosition, newSpaceIndex, isSymbolLast) {
    if (hasSpace(formatParts)) {
        const currentSpaceIndex = indexOfNumberFormatPart(formatParts, LITERAL_TYPE, isSymbolLast);
        if (symbolPosition === "end" /* End */) {
            // Strip RTL characters from space (e.g. in case of Arabic locale)
            formatParts[currentSpaceIndex].value = formatParts[currentSpaceIndex].value.replace(SPECIAL_CHARS_REGEX, '');
        }
        // Update space position.
        formatParts = moveArrayElement(formatParts, currentSpaceIndex, newSpaceIndex);
        // Remove the space from the pattern for COMPACT type
        if (type === "compact" /* Compact */) {
            formatParts.splice(newSpaceIndex, 1);
        }
    }
    return formatParts;
}
/**
 * remove the space between the currency symbol and numerical portion of the price format pattern.
 * @param formatParts - Array of price format components
 * @return Array of price format components without space in the pattern
 */
function removeSpaceBetweenSymbolAndAmount(formatParts) {
    if (hasSpace(formatParts)) {
        let spaceIndex;
        if (!isSymbolLast(formatParts)) {
            spaceIndex = indexOfNumberFormatPart(formatParts, LITERAL_TYPE, false);
        }
        else {
            spaceIndex = indexOfNumberFormatPart(formatParts, LITERAL_TYPE, true);
        }
        if (formatParts[spaceIndex].type === LITERAL_TYPE && formatParts[spaceIndex].value === NO_BREAK_SPACE) {
            formatParts.splice(spaceIndex, 1);
        }
    }
    return formatParts;
}
/**
 * Update the price format pattern with a space between the currency symbol and numerical
 * portion of the format if there already isn't a space present there.
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @return {Intl.NumberFormatPart[]} Array of price format components with space in the pattern
 */
function insertSpaceInFormat(formatParts) {
    const literalPart = {
        type: LITERAL_TYPE,
        value: NO_BREAK_SPACE
    };
    if (!hasSpace(formatParts)) {
        let symbolIndex;
        if (!isSymbolLast(formatParts)) {
            symbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, false);
            // Insert space after currency symbol
            formatParts.splice(symbolIndex + 1, 0, literalPart);
        }
        else {
            symbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, true);
            // Insert space before currency symbol
            formatParts.splice(symbolIndex, 0, literalPart);
        }
    }
    return formatParts;
}
/**
 * Returns a NumberFormatOptions object configured for the currency and type passed in.
 * @param {Currency} currency - The requested currency
 * @param {Type} type - The requested formatting type
 * @return {Intl.NumberFormatOptions} The number format options
 */
function createCurrencyNumberFormatOptions(currency, type) {
    return Object.assign({ currency: currency.currencyCode }, createNumberFormatOptions(type));
}
/**
 * Returns a NumberFormatOptions object configured for the currency and type passed in
 * @param {Currency} currency - The requested currency
 * @param {Type} type - The requested formatting type
 * @return {Intl.NumberFormatOptions} The number format options
 */
function createNumberFormatOptions(type) {
    return Object.assign({ style: CURRENCY_STYLE, currencyDisplay: getCurrencyDisplay(type), useGrouping: getUseGrouping(type) }, getMinimumAndMaximumFractionDigits(type));
}
/**
 * Returns a CurrencyDisplay configured for the Type passed in.
 * @param {Type} type - The requested formatting type
 * @return {string} The currency display value
 */
function getCurrencyDisplay(type) {
    switch (type) {
        case "iso" /* Iso */:
            return 'code';
        case "plural" /* Plural */:
            return 'name';
        default:
            return 'symbol';
    }
}
/**
 * Returns whether to use grouping configured for the Type passed in.
 * @param {Type} type - The requested formatting type
 * @return {boolean} Boolean for whether to use grouping or not
 */
function getUseGrouping(type) {
    switch (type) {
        case "compact" /* Compact */:
            return false;
        default:
            return true;
    }
}
/**
 * Returns what minimum and maximumFractionDigits to use configured for the Type passed in.
 * @param {Type} type - The requested formatting type
 * @return {Intl.NumberFormatOptions} The number format options
 */
function getMinimumAndMaximumFractionDigits(type) {
    switch (type) {
        case "compact" /* Compact */:
            return {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0
            };
        default:
            return {};
    }
}
/**
 * Rounds the amount down or up based on Type and amount.
 * @param {number} amount - The requested amount
 * @param {Type} type - The requested formatting type
 * @return {number} The rounded amount
 */
function roundAmount(amount, type) {
    switch (type) {
        case "compact" /* Compact */:
            // Rounding towards zero.
            if (amount < 0) {
                const roundedUp = Math.ceil(amount);
                // Math.ceil can result in '-0'
                return roundedUp ? roundedUp : 0;
            }
            return Math.floor(amount);
        default:
            return amount;
    }
}
/**
 * Returns whether to use a custom symbol in formatted price.
 * @param {Type} type - The requested formatting type
 */
function shouldApplyCustomSymbol(type) {
    switch (type) {
        case "iso" /* Iso */:
            return false;
        case "plural" /* Plural */:
            return false;
        default:
            return true;
    }
}
/**
 * Returns whether the given number format pattern contains a space in the currency format
 * @param {Intl.NumberFormatPart[]} formatPartType - Array of price format components
 */
function hasSpace(formatParts) {
    let spaceIndex = 1;
    if (!isSymbolLast(formatParts)) {
        // For Arabic locales, the first symbol can be a left-to-right mark of type 'literal'
        if (formatParts[spaceIndex - 1].type === LITERAL_TYPE) {
            spaceIndex++;
        }
        // For negative numbers, there can be a minus sign before the currency symbol
        if (formatParts[spaceIndex - 1].type === MINUS_SIGN_TYPE) {
            spaceIndex++;
        }
    }
    else {
        spaceIndex = formatParts.length;
        // Check for Literal, some Arabic locales have a Literal on end.
        if (formatParts[spaceIndex - 1].type === LITERAL_TYPE) {
            spaceIndex--;
        }
        // Check for space before currency symbol if symbol is on end.
        spaceIndex = spaceIndex - 2;
    }
    return formatParts[spaceIndex].type === LITERAL_TYPE;
}
/**
 * Returns whether the symbolPosition override needs to be applied for the given price format Type.
 * @param {Type} type - The requested price format type
 */
function shouldApplySymbolPositionOverride(type) {
    switch (type) {
        case "iso" /* Iso */:
            return false;
        default:
            return true;
    }
}
/**
 * Returns whether the currency symbol is at the last (right) of the price format.
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @return {boolean} true in case currency symbol is at last, otherwise false.
 */
function isSymbolLast(formatParts) {
    let symbolIndex = formatParts.length - 1;
    // For Arabic locales, the last symbol can be of type 'literal'.
    if (formatParts[symbolIndex].type === LITERAL_TYPE) {
        symbolIndex--;
    }
    return formatParts[symbolIndex].type === CURRENCY_TYPE;
}
/**
 * Checks whether given formatParts contains a fractional part
 * @param {Intl.NumberFormatPart[]} formatParts Array of price format components
 */
function hasFraction(formatParts) {
    return hasNumberFormatPart(formatParts, FRACTION_TYPE);
}
/**
 * Checks whether given formatParts contains a decimal separator part
 * @param {Intl.NumberFormatPart[]} formatParts Array of price format components
 */
function hasDecimalSeparator(formatParts) {
    return hasNumberFormatPart(formatParts, DECIMAL_TYPE);
}
/**
 * Checks whether given formatParts contains a part with the requested type.
 * @param {Intl.NumberFormatPart[]} formatParts Array of price format components
 * @param {Intl.NumberFormatPartTypes} type Requested type
 */
function hasNumberFormatPart(formatParts, type) {
    if (!formatParts) {
        throw new Error('Format Parts must not be emtpy');
    }
    for (const formatPart of formatParts) {
        if (formatPart.type === type) {
            return true;
        }
    }
    return false;
}
/**
 * Utility function to move elements from position A to position B within the given array.
 * @param {Intl.NumberFormatPart[]} array - Input array
 * @param {number} from - Index of element to be moved
 * @param {number} to - Destination index for the chosen element
 * @return {Intl.NumberFormatPart[]} array with rearranged elements as per given position of element
 */
function moveArrayElement(array, from, to) {
    if (to === from) {
        return array;
    }
    const target = array[from];
    const increment = to < from ? -1 : 1;
    for (let k = from; k !== to; k += increment) {
        array[k] = array[k + increment];
    }
    array[to] = target;
    return array;
}
/**
 * Utility function to find the index of the a given number format type within the currency format pattern.
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @param {Intl.NumberFormatPartTypes} type - The input number format part type
 * @param {boolean} isSymbolLast - True in case symbol position is last false otherwise
 * @return {number} index of given number format type, -1 if not found
 */
function indexOfNumberFormatPart(formatParts, type, isSymbolLast) {
    let startIndex;
    let endIndex;
    let incrementCounter;
    if (isSymbolLast) {
        // Start the loop from end.
        startIndex = formatParts.length - 1;
        endIndex = 0;
        incrementCounter = -1;
    }
    else {
        // Start the loop from front.
        startIndex = 0;
        endIndex = formatParts.length - 1;
        incrementCounter = 1;
    }
    while (startIndex !== endIndex) {
        if (formatParts[startIndex].type === type) {
            // Ignore cases for 'literal' types as the first or last character (e.g. Arabic locale LTR mark)
            if (type === LITERAL_TYPE && (startIndex === 0 || startIndex === formatParts.length - 1)) {
                startIndex += incrementCounter;
                continue;
            }
            else {
                return startIndex;
            }
        }
        startIndex += incrementCounter;
    }
    // Check for last index.
    if (formatParts[startIndex].type === type) {
        return startIndex;
    }
    return -1;
}

/**
 * Map PriceModifierType to i18n.price-format price type.
 *
 * @param {PriceModifierType} modifierType price modifier.
 * @returns {Type} i18n.price-format price type.
 */
function getI18nPriceType(modifierType) {
    switch (modifierType) {
        case 'compact':
            return "compact" /* Compact */;
        case 'in_sentence':
            return "inSentence" /* InSentence */;
        case 'iso':
            return "iso" /* Iso */;
        case 'plural':
            return "plural" /* Plural */;
        case 'aui':
        case 'amazon_symbol':
        case 'default':
        default:
            return "amazonSymbol" /* AmazonSymbol */;
    }
}
/**
 * I18n price formatter integrating with IUTypescript directly.
 */
class I18nPriceFormatter {
    /**
     * Constructor
     *
     * @param {LocalesOrLocalizationContext} localesOrLocalizationContext - A string
     * with a BCP 47 language tag, an array of one such string, or a localization context.
     * If it's not provided, then it's resolved to a default locale.
     *
     * @param {PriceFormatOptions} options - price format options
     */
    constructor(localesOrLocalizationContext, options) {
        if (!localesOrLocalizationContext) {
            localesOrLocalizationContext = new Intl.NumberFormat().resolvedOptions()
                .locale;
        }
        const type = options && options.style
            ? getI18nPriceType(options.style)
            : getI18nPriceType('default');
        let locale;
        let marketplace;
        if (isLocalizationContext(localesOrLocalizationContext)) {
            locale = localesOrLocalizationContext.getLocale();
            marketplace = localesOrLocalizationContext
                .asAttributesMap()
                .get(MARKETPLACE_ATTRIBUTE);
        }
        else {
            // TODO: Add logging functionality and log warn message here indicating
            // localesOrLocalizationContext[] is used as an array of one locale string at this stage,
            // so only the first locale string is picked from localesOrLocalizationContext[]
            locale =
                typeof localesOrLocalizationContext === 'string'
                    ? localesOrLocalizationContext
                    : localesOrLocalizationContext.length === 0
                        ? new Intl.NumberFormat().resolvedOptions().locale
                        : localesOrLocalizationContext[0];
        }
        if (marketplace) {
            this.priceFormat = PriceFormat.getInstanceByTypeAndMarketplace(locale, type, marketplace);
        }
        else {
            this.priceFormat = PriceFormat.getInstanceByType(locale, type);
        }
    }
    format(price) {
        return this.priceFormat.format(price);
    }
}

/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/
// AMAZON_CHANGE_END
function mergeConfig(c1, c2) {
    if (!c2) {
        return c1;
    }
    return Object.assign(Object.assign(Object.assign({}, (c1 || {})), (c2 || {})), Object.keys(c1).reduce((all, k) => {
        all[k] = Object.assign(Object.assign({}, c1[k]), (c2[k] || {}));
        return all;
    }, {}));
}
function mergeConfigs(defaultConfig, configs) {
    if (!configs) {
        return defaultConfig;
    }
    return Object.keys(defaultConfig).reduce((all, k) => {
        all[k] = mergeConfig(defaultConfig[k], configs[k]);
        return all;
    }, Object.assign({}, defaultConfig));
}
function createFastMemoizeCache(store) {
    return {
        create() {
            return {
                has(key) {
                    return key in store;
                },
                get(key) {
                    return store[key];
                },
                set(key, value) {
                    store[key] = value;
                },
            };
        },
    };
}
function createDefaultFormatters(cache = {
    number: {},
    dateTime: {},
    pluralRules: {},
    price: {},
}) {
    return {
        getNumberFormat: memoize((...args) => new Intl.NumberFormat(...args), {
            cache: createFastMemoizeCache(cache.number),
            strategy: strategies.variadic,
        }),
        getDateTimeFormat: memoize((...args) => new Intl.DateTimeFormat(...args), {
            cache: createFastMemoizeCache(cache.dateTime),
            strategy: strategies.variadic,
        }),
        getPluralRules: memoize((...args) => new Intl.PluralRules(...args), {
            cache: createFastMemoizeCache(cache.pluralRules),
            strategy: strategies.variadic,
        }),
        // AMAZON_CHANGE_START
        getPriceFormat: memoize((...args) => new I18nPriceFormatter(...args), {
            cache: createFastMemoizeCache(cache.price),
            strategy: strategies.variadic,
        }),
    };
}
class IntlMessageFormat$1 {
    constructor(message, 
    // AMAZON_CHANGE_START
    localesOrLocalizationContext = IntlMessageFormat$1.defaultLocale, 
    // AMAZON_CHANGE_END
    overrideFormats, opts) {
        this.formatterCache = {
            number: {},
            dateTime: {},
            pluralRules: {},
            price: {},
        };
        this.format = (values) => {
            const parts = this.formatToParts(values);
            // Hot path for straight simple msg translations
            if (parts.length === 1) {
                return parts[0].value;
            }
            const result = parts.reduce((all, part) => {
                if (!all.length ||
                    part.type !== PART_TYPE.literal ||
                    typeof all[all.length - 1] !== 'string') {
                    all.push(part.value);
                }
                else {
                    all[all.length - 1] += part.value;
                }
                return all;
            }, []);
            if (result.length <= 1) {
                return result[0] || '';
            }
            return result;
        };
        this.formatToParts = (values) => formatToParts(this.ast, 
        // AMAZON_CHANGE_START
        this.localesOrLocalizationContext, 
        // AMAZON_CHANGE_END
        this.formatters, this.formats, values, undefined, this.message);
        // AMAZON_CHANGE_START
        /**
         * Format to VDOM parts for consumers requring rendering with VDOM.
         * It is consumer's responsibility to pass the "hyperscriptRuntime"
         * with hyperscript function "createElement". Message formatting
         * library would just invoke this hyperscript function upon detection
         * of the HTML tags.
         *
         * @param hyperscriptRuntime Hyperscript runtime with "createElement(...)" h function.
         * @param values Arguments.
         * @returns Formatter parts are returned with the aid of provided hyperscript.
         */
        this.formatToVdomParts = (hyperscriptRuntime, values) => formatToVdomParts(this.ast, this.localesOrLocalizationContext, this.formatters, this.formats, hyperscriptRuntime, values, undefined, this.message);
        // AMAZON_CHANGE_END
        this.resolvedOptions = () => {
            const locales = isLocalizationContext(this.localesOrLocalizationContext)
                ? this.localesOrLocalizationContext.getLocale()
                : this.localesOrLocalizationContext;
            return { locale: Intl.NumberFormat.supportedLocalesOf(locales)[0] };
        };
        this.getAst = () => this.ast;
        if (typeof message === 'string') {
            this.message = message;
            if (!IntlMessageFormat$1.__parse) {
                throw new TypeError('IntlMessageFormat.__parse must be set to process `message` of type `string`');
            }
            // Parse string messages into an AST.
            this.ast = IntlMessageFormat$1.__parse(message, {
                ignoreTag: opts !== undefined ? opts.ignoreTag : opts,
            });
        }
        else {
            this.ast = message;
        }
        if (!Array.isArray(this.ast)) {
            throw new TypeError('A message must be provided as a String or AST.');
        }
        // Creates a new object with the specified `formats` merged with the default
        // formats.
        this.formats = mergeConfigs(IntlMessageFormat$1.formats, overrideFormats);
        // Defined first because it's used to build the format pattern.
        // AMAZON_CHANGE_START
        this.localesOrLocalizationContext = localesOrLocalizationContext;
        // AMAZON_CHANGE_END
        this.formatters =
            (opts && opts.formatters) || createDefaultFormatters(this.formatterCache);
    }
    static get defaultLocale() {
        if (!IntlMessageFormat$1.memoizedDefaultLocale) {
            IntlMessageFormat$1.memoizedDefaultLocale = new Intl.NumberFormat().resolvedOptions().locale;
        }
        return IntlMessageFormat$1.memoizedDefaultLocale;
    }
}
IntlMessageFormat$1.memoizedDefaultLocale = null;
IntlMessageFormat$1.__parse = parse;
// Default format options used as the prototype of the `formats` provided to the
// constructor. These are used when constructing the internal Intl.NumberFormat
// and Intl.DateTimeFormat instances.
IntlMessageFormat$1.formats = {
    number: {
        currency: {
            style: 'currency',
        },
        percent: {
            style: 'percent',
        },
    },
    date: {
        short: {
            month: 'numeric',
            day: 'numeric',
            year: '2-digit',
        },
        medium: {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        },
        long: {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        },
        full: {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        },
    },
    time: {
        short: {
            hour: 'numeric',
            minute: 'numeric',
        },
        medium: {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        },
        long: {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZoneName: 'short',
        },
        full: {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZoneName: 'short',
        },
    },
    // AMAZON_CHANGE_START
    price: {
        default: {
            style: 'default',
        },
        aui: {
            style: 'aui',
        },
        iso: {
            style: 'iso',
        },
        plural: {
            style: 'plural',
        },
        amazon_symbol: {
            style: 'amazon_symbol',
        },
        compact: {
            style: 'compact',
        },
        in_sentence: {
            style: 'in_sentence',
        },
    },
};
// AMAZON_CHANGE_START
function isLocalizationContext(localesOrLocalizationContext) {
    return (localesOrLocalizationContext.getLocale !==
        undefined);
}
// AMAZON_CHANGE_END

/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/
var IntlMessageFormat = IntlMessageFormat$1;

/**
 * Wrapper around actual message formatting library.
 */
//TODO: Support for timeZone with formatter.
class MessageFormat {
    constructor(context) {
        this.context = context;
    }
    /**
     * Format message and return plain string. Message can be simple string or pre-parsed AST.
     * @param message message to format.
     * @param args arguments for formatting message.
     * @returns formatted string.
     */
    format(message, args) {
        const formatter = new IntlMessageFormat(message, this.context);
        return formatter.format(args);
    }
}

/**
 * This is a temporary class to substitute {@link StaticMessageBundle}. This should be removed once we clean up the
 * {@link StaticMessageBundle} class, by removing Kata related functionalities.
 *
 * Immutable MessageBundle for localization context specific Panther resources at runtime.
 * Once MessageBundle is formed using fallback mechanism, it is cached.
 */
class StaticMessageBundle {
    constructor(messages, baseName, context) {
        this.messages = messages;
        this.baseName = baseName;
        this.context = context;
        this.messageFormat = new MessageFormat(context);
    }
    /**
     * Returns the message identified by key. If the messageKey is not found in the
     * bundle, error is thrown.
     *
     * @param messageKey key of the message in the bundle.
     * @returns message for provided key. message can be pre-parsed AST.
     * @throws Exception if message key not found in bundle.
     */
    getMessage(messageKey) {
        const message = this.messages.get(messageKey);
        if (message === undefined) {
            throw new Error(`Resource does not exist: bundle=${this.baseName}, key=${messageKey}, context=${this.context}`);
        }
        return message;
    }
    /**
     * Finds the message for messageKey in the bundle and format using ICU message formatting.
     * @param messageKey key of the message in the bundle.
     * @param args arguments for message formatting.
     * @returns Formatted message.
     * @throws Exception if message key if not found in bundle.
     */
    formatMessage(messageKey, args) {
        const message = this.getMessage(messageKey);
        return this.messageFormat.format(message, args);
    }
}

/**
 * Default values for the manifest.
 */
const DEFAULT_MANIFEST = Object.freeze({
    runtimeResourceFormat: 'JsonFileHierarchy',
    fallbackMechanismAttributes: [['i18nDomain']],
});

var loglevel$1 = {exports: {}};

/*
* loglevel - https://github.com/pimterry/loglevel
*
* Copyright (c) 2013 Tim Perry
* Licensed under the MIT license.
*/

(function (module) {
(function (root, definition) {
    if (module.exports) {
        module.exports = definition();
    } else {
        root.log = definition();
    }
}(commonjsGlobal, function () {

    // Slightly dubious tricks to cut down minimized file size
    var noop = function() {};
    var undefinedType = "undefined";
    var isIE = (typeof window !== undefinedType) && (typeof window.navigator !== undefinedType) && (
        /Trident\/|MSIE /.test(window.navigator.userAgent)
    );

    var logMethods = [
        "trace",
        "debug",
        "info",
        "warn",
        "error"
    ];

    // Cross-browser bind equivalent that works at least back to IE6
    function bindMethod(obj, methodName) {
        var method = obj[methodName];
        if (typeof method.bind === 'function') {
            return method.bind(obj);
        } else {
            try {
                return Function.prototype.bind.call(method, obj);
            } catch (e) {
                // Missing bind shim or IE8 + Modernizr, fallback to wrapping
                return function() {
                    return Function.prototype.apply.apply(method, [obj, arguments]);
                };
            }
        }
    }

    // Trace() doesn't print the message in IE, so for that case we need to wrap it
    function traceForIE() {
        if (console.log) {
            if (console.log.apply) {
                console.log.apply(console, arguments);
            } else {
                // In old IE, native console methods themselves don't have apply().
                Function.prototype.apply.apply(console.log, [console, arguments]);
            }
        }
        if (console.trace) console.trace();
    }

    // Build the best logging method possible for this env
    // Wherever possible we want to bind, not wrap, to preserve stack traces
    function realMethod(methodName) {
        if (methodName === 'debug') {
            methodName = 'log';
        }

        if (typeof console === undefinedType) {
            return false; // No method possible, for now - fixed later by enableLoggingWhenConsoleArrives
        } else if (methodName === 'trace' && isIE) {
            return traceForIE;
        } else if (console[methodName] !== undefined) {
            return bindMethod(console, methodName);
        } else if (console.log !== undefined) {
            return bindMethod(console, 'log');
        } else {
            return noop;
        }
    }

    // These private functions always need `this` to be set properly

    function replaceLoggingMethods(level, loggerName) {
        /*jshint validthis:true */
        for (var i = 0; i < logMethods.length; i++) {
            var methodName = logMethods[i];
            this[methodName] = (i < level) ?
                noop :
                this.methodFactory(methodName, level, loggerName);
        }

        // Define log.log as an alias for log.debug
        this.log = this.debug;
    }

    // In old IE versions, the console isn't present until you first open it.
    // We build realMethod() replacements here that regenerate logging methods
    function enableLoggingWhenConsoleArrives(methodName, level, loggerName) {
        return function () {
            if (typeof console !== undefinedType) {
                replaceLoggingMethods.call(this, level, loggerName);
                this[methodName].apply(this, arguments);
            }
        };
    }

    // By default, we use closely bound real methods wherever possible, and
    // otherwise we wait for a console to appear, and then try again.
    function defaultMethodFactory(methodName, level, loggerName) {
        /*jshint validthis:true */
        return realMethod(methodName) ||
               enableLoggingWhenConsoleArrives.apply(this, arguments);
    }

    function Logger(name, defaultLevel, factory) {
      var self = this;
      var currentLevel;
      defaultLevel = defaultLevel == null ? "WARN" : defaultLevel;

      var storageKey = "loglevel";
      if (typeof name === "string") {
        storageKey += ":" + name;
      } else if (typeof name === "symbol") {
        storageKey = undefined;
      }

      function persistLevelIfPossible(levelNum) {
          var levelName = (logMethods[levelNum] || 'silent').toUpperCase();

          if (typeof window === undefinedType || !storageKey) return;

          // Use localStorage if available
          try {
              window.localStorage[storageKey] = levelName;
              return;
          } catch (ignore) {}

          // Use session cookie as fallback
          try {
              window.document.cookie =
                encodeURIComponent(storageKey) + "=" + levelName + ";";
          } catch (ignore) {}
      }

      function getPersistedLevel() {
          var storedLevel;

          if (typeof window === undefinedType || !storageKey) return;

          try {
              storedLevel = window.localStorage[storageKey];
          } catch (ignore) {}

          // Fallback to cookies if local storage gives us nothing
          if (typeof storedLevel === undefinedType) {
              try {
                  var cookie = window.document.cookie;
                  var location = cookie.indexOf(
                      encodeURIComponent(storageKey) + "=");
                  if (location !== -1) {
                      storedLevel = /^([^;]+)/.exec(cookie.slice(location))[1];
                  }
              } catch (ignore) {}
          }

          // If the stored level is not valid, treat it as if nothing was stored.
          if (self.levels[storedLevel] === undefined) {
              storedLevel = undefined;
          }

          return storedLevel;
      }

      function clearPersistedLevel() {
          if (typeof window === undefinedType || !storageKey) return;

          // Use localStorage if available
          try {
              window.localStorage.removeItem(storageKey);
              return;
          } catch (ignore) {}

          // Use session cookie as fallback
          try {
              window.document.cookie =
                encodeURIComponent(storageKey) + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC";
          } catch (ignore) {}
      }

      /*
       *
       * Public logger API - see https://github.com/pimterry/loglevel for details
       *
       */

      self.name = name;

      self.levels = { "TRACE": 0, "DEBUG": 1, "INFO": 2, "WARN": 3,
          "ERROR": 4, "SILENT": 5};

      self.methodFactory = factory || defaultMethodFactory;

      self.getLevel = function () {
          return currentLevel;
      };

      self.setLevel = function (level, persist) {
          if (typeof level === "string" && self.levels[level.toUpperCase()] !== undefined) {
              level = self.levels[level.toUpperCase()];
          }
          if (typeof level === "number" && level >= 0 && level <= self.levels.SILENT) {
              currentLevel = level;
              if (persist !== false) {  // defaults to true
                  persistLevelIfPossible(level);
              }
              replaceLoggingMethods.call(self, level, name);
              if (typeof console === undefinedType && level < self.levels.SILENT) {
                  return "No console available for logging";
              }
          } else {
              throw "log.setLevel() called with invalid level: " + level;
          }
      };

      self.setDefaultLevel = function (level) {
          defaultLevel = level;
          if (!getPersistedLevel()) {
              self.setLevel(level, false);
          }
      };

      self.resetLevel = function () {
          self.setLevel(defaultLevel, false);
          clearPersistedLevel();
      };

      self.enableAll = function(persist) {
          self.setLevel(self.levels.TRACE, persist);
      };

      self.disableAll = function(persist) {
          self.setLevel(self.levels.SILENT, persist);
      };

      // Initialize with the right level
      var initialLevel = getPersistedLevel();
      if (initialLevel == null) {
          initialLevel = defaultLevel;
      }
      self.setLevel(initialLevel, false);
    }

    /*
     *
     * Top-level API
     *
     */

    var defaultLogger = new Logger();

    var _loggersByName = {};
    defaultLogger.getLogger = function getLogger(name) {
        if ((typeof name !== "symbol" && typeof name !== "string") || name === "") {
          throw new TypeError("You must supply a name when creating a logger.");
        }

        var logger = _loggersByName[name];
        if (!logger) {
          logger = _loggersByName[name] = new Logger(
            name, defaultLogger.getLevel(), defaultLogger.methodFactory);
        }
        return logger;
    };

    // Grab the current global log variable in case of overwrite
    var _log = (typeof window !== undefinedType) ? window.log : undefined;
    defaultLogger.noConflict = function() {
        if (typeof window !== undefinedType &&
               window.log === defaultLogger) {
            window.log = _log;
        }

        return defaultLogger;
    };

    defaultLogger.getLoggers = function getLoggers() {
        return _loggersByName;
    };

    // ES6 default export, for compatibility
    defaultLogger['default'] = defaultLogger;

    return defaultLogger;
}));
}(loglevel$1));

var loglevel = loglevel$1.exports;

const LOGGER_NAME = 'arb-js';
const LOGGER_PREFIX = 'arb-js';
const logger = loglevel.getLogger(LOGGER_NAME);
const logLevelLogger = {
    trace(...args) {
        this.output('trace', args);
    },
    debug(...args) {
        this.output('debug', args);
    },
    info(...args) {
        this.output('info', args);
    },
    warn(...args) {
        this.output('warn', args);
    },
    error(...args) {
        this.output('error', args);
    },
    isDebugEnabled() {
        return logger.getLevel() <= logger.levels.DEBUG;
    },
    isInfoEnabled() {
        return logger.getLevel() <= logger.levels.INFO;
    },
    output(type, args) {
        args[0] = `[${LOGGER_PREFIX}] ${args[0]}`;
        // @ts-ignore TS doesn't like accessing method via object property accessors
        logger[type](...args);
    },
};
var logger$1 = logLevelLogger;

const DEFAULTS = {};
/**
 * Remote resource reader for AsyncMessageBundle. Create an instance using {@link createFetchRemoteResourceReader} or
 * {@link createNodeHttpRemoteResourceReader}.
 */
class RemoteResourceReader {
    constructor(options) {
        this.RETRY_LIMIT = 1;
        /**
         * For caching resource requests by url — so we don't make duplicate requests
         * @private
         */
        this.loaded = {};
        this.options = Object.assign(Object.assign({}, DEFAULTS), options);
    }
    // TODO: Implement allowing to define individual fallback strategy on a bundle basis
    /**
     * @inheritDoc
     * @param _bundle
     */
    readManifest(_bundle) {
        return DEFAULT_MANIFEST;
    }
    /**
     * @inheritDoc
     * @param fallbackItem
     */
    async readResource(fallbackItem) {
        for (let i = 0; i <= this.RETRY_LIMIT; i++) {
            let url;
            try {
                url = this.options.resolveBundleUrl(this.options, fallbackItem);
                if (url === undefined) {
                    logger$1.error('No resource endpoint found. Fallback item: %O', fallbackItem);
                    return;
                }
                if (this.loaded[url] === undefined) {
                    logger$1.info(`Fetching ${url} for fallback item: %O`, fallbackItem);
                    this.loaded[url] = this.options.resolveResourcePack(url);
                }
                else {
                    logger$1.info(`Request already fetched for fallback item: %O`, fallbackItem);
                }
                const resourcePack = await this.loaded[url];
                if (resourcePack === null) {
                    logger$1.error('Loading resource has failed. Fallback item: %O', fallbackItem);
                    return;
                }
                return resourcePack;
            }
            catch (e) {
                if (e.retry && url) {
                    logger$1.info(`Retrying request for ${url}. Fallback item: %O`, fallbackItem);
                    this.loaded[url] = undefined; // reset cache for the specific url
                    continue;
                }
                logger$1.error(`Failed to load resource. Error: ${e.message} Fallback item: %O`, fallbackItem);
                break;
            }
        }
        return undefined;
    }
}

class ResourceFetchError extends Error {
    constructor(message, retry = false) {
        super(message);
        this.retry = retry;
        this.retry = retry;
    }
}

/**
 * Creates RemoteResourceReader instance that uses the browser fetch API to fetch resources.
 * @param options
 */
function createFetchRemoteResourceReader(options) {
    return new RemoteResourceReader(Object.assign({ async resolveResourcePack(url) {
            const response = await fetch(url);
            const { ok, status, statusText } = response;
            if (!ok) {
                const retry = status >= 500 && status < 600; // don't retry for 4xx codes
                logger$1.error(`Failed to fetch ${url}: %O`, { status, statusText });
                throw new ResourceFetchError(`Failed to fetch ${url}`, retry);
            }
            return response.json();
        } }, options));
}

async function measurePerformance(measureName, fn) {
    if (typeof performance !== 'undefined') {
        const start = performance.now();
        await fn();
        const end = performance.now();
        logger$1.info(`${measureName}: ${end - start} ms`);
    }
    else {
        console.time(measureName);
        await fn();
        console.timeEnd(measureName);
    }
}

/**
 * Message loader to load and cache the localization context specific Panther resources
 * with fallback mechanism.
 */
class AsyncCombinedMessageBundleLoader {
    /**
     * Create MessageLoader with the resource reader and the fallback handler. Resource reader
     * is used to read the Panther resources and the Manifest while forming the MessageBundle
     * using fallback mechanism.
     *
     * @param resourceReader  To read the Panther resources and the Manifest.
     * @param fallbackHandler  To form the fallback chain to load the resource.
     */
    constructor(resourceReader, fallbackHandler) {
        this.resourceReader = resourceReader;
        this.fallbackHandler = fallbackHandler;
        this.resourcesCache = new LoadingCache(this.resourceReader.readResource.bind(this.resourceReader), {
            maxSize: AsyncCombinedMessageBundleLoader.RESOURCE_CACHE_SIZE,
            keyFn: FallbackItem.cacheKey,
        });
    }
    /**
     * Load resources using the resource reader and fallback mechanism if not present in the cache. This method may
     * initiate multiple fetch requests for current locale, fallback locale & root locale.
     * @param bundle Panther resource bundle.
     * @param context Localization context.
     * @returns MessageBundle
     * @throws error for invalid manifest.
     */
    async load(bundle, context) {
        const contextMessages = new Map();
        const manifest = this.resourceReader.readManifest(bundle);
        if (!manifest) {
            throw new Error(CONSTANTS.noManifestError);
        }
        const fallbackChain = this.fallbackHandler.getFallbackChains(bundle, manifest.fallbackMechanismAttributes, context);
        // Start fetching all required resource packs
        logger$1.info(`Requesting resource packs for "${bundle}" bundle. Localization context: %0`, context);
        await measurePerformance(`Resource pack requests for "${bundle}" bundle`, async () => {
            const promises = [];
            for (const fallbackItem of fallbackChain) {
                promises.push(this.resourcesCache.get(fallbackItem));
            }
            await Promise.all(promises);
        });
        for (const fallbackItem of fallbackChain) {
            const resourcePack = (await this.resourcesCache.get(fallbackItem));
            if (resourcePack === undefined ||
                resourcePack[fallbackItem.locale] === undefined ||
                resourcePack[fallbackItem.locale][fallbackItem.bundle] === undefined) {
                logger$1.debug(`No bundled resources were found for %0`, fallbackItem);
                continue;
            }
            const messages = resourcePack[fallbackItem.locale][fallbackItem.bundle];
            for (const [id, value] of Object.entries(messages)) {
                if (!contextMessages.has(id)) {
                    contextMessages.set(id, this.findMessage(value, fallbackItem));
                }
            }
        }
        return new StaticMessageBundle(contextMessages, bundle, context);
    }
    /**
     * Find assignable message from ResourcePackValue which may hold variant overrides.
     * @param value resource pack value with variant overrides
     * @param fallbackItem
     * @private
     */
    findMessage(value, fallbackItem) {
        const valueType = typeof value;
        if (valueType === 'string') {
            return value;
        }
        else if (valueType === 'object') {
            const variant = this.findMatchingVariant(value, fallbackItem);
            // Fallback to original value if no variant was found
            return variant ? variant.value : value.value;
        }
        throw new Error('Unsupported resource pack value type');
    }
    /**
     * Traverse overrides from the last entry and return the first match
     * @param puffjWithOverrides
     * @param fallbackItem
     * @private
     */
    findMatchingVariant(puffjWithOverrides, fallbackItem) {
        const overrides = puffjWithOverrides.overrides;
        for (let i = overrides.length - 1; i >= 0; i--) {
            const override = overrides[i];
            let isContextFulfilled = true;
            for (const [variantName, variantValue] of Object.entries(override.localizationContext)) {
                if (fallbackItem.variants &&
                    (!fallbackItem.variants.has(variantName) || fallbackItem.variants.get(variantName) !== variantValue)) {
                    isContextFulfilled = false;
                    break;
                }
            }
            if (isContextFulfilled) {
                return override;
            }
        }
        return undefined;
    }
}
/**
 * Maximum number of resources to cache.
 */
AsyncCombinedMessageBundleLoader.RESOURCE_CACHE_SIZE = 512;

/**
 * MessageBundleManager that loads MessageBundle from a resource pack that may consist of multiple locales & bundles.
 */
class AsyncCombinedMessageBundleManager {
    /**
     * Creates an instance of a combined message bundle manager.
     * @param localizationContextProvider
     * @param asyncResourceReader resource pack reader
     */
    constructor(localizationContextProvider, asyncResourceReader) {
        this.localizationContextProvider = localizationContextProvider;
        this.asyncResourceReader = asyncResourceReader;
        const fallbackGen = new ResourceFallbackHandler();
        this.bundleLoader = new AsyncCombinedMessageBundleLoader(asyncResourceReader, fallbackGen);
        this.bundleCache = new LoadingCache(this.generateMessageBundle.bind(this), {
            maxSize: AsyncCombinedMessageBundleManager.MESSAGE_BUNDLE_CACHE_SIZE,
            keyFn: MessageBundleCacheKey.generateCacheKey,
        });
    }
    /**
     * Generates message bundle using MessageLoader.
     * @param bundleKey
     * @protected
     */
    async generateMessageBundle(bundleKey) {
        return this.bundleLoader.load(bundleKey.bundle, bundleKey.context);
    }
    /**
     * Load Panther resources in the form of Message bundle and cache.
     * @param bundle Panther message bundle name
     * @param context localization context
     */
    async getBundle(bundle, context) {
        const currentContext = context || this.localizationContextProvider.getLocalizationContext();
        const messageBundleKey = new MessageBundleCacheKey(bundle, currentContext);
        const messageBundle = await this.bundleCache.get(messageBundleKey);
        return messageBundle;
    }
}
/**
 * Maximum number of bundles to cache.
 */
// TODO: Make this heuristic configurable?
AsyncCombinedMessageBundleManager.MESSAGE_BUNDLE_CACHE_SIZE = 512;

// TODO: Needs to be able to resolve the correct url if localeBranches was
//  configured to something like: fr-CA: [fr-CA, fr-FR, fr]; fr: [..., fr]
/**
 * Get resource pack url from manifest file.
 * @param arbManifest
 * @param fallbackItem
 */
function getUrlFromManifest(arbManifest, fallbackItem) {
    var localeData = arbManifest && arbManifest.endpointMap && arbManifest.endpointMap[fallbackItem.locale];
    return localeData && localeData[fallbackItem.bundle];
}
/**
 * Create a message bundle manager to retrieve compiled PUFF-J
 * resources in run time. mbm.getBundle() call will initiate request
 * to download compiled PUFF-J files. By default it will fetch by
 * prefixing the option.urlPrefix to the generated file name (en.json) using
 * browser's fetch() method.
 *
 * Usage:
 * const arbManifest = .... // import build time generated manifest file
 * const mbm = createAsyncMessageBundleManager({
 *   arbManifest: arbManifest,
 *   localizationContextProvider: readFromSlateToken,
 *   urlPrefix: 'translations',
 * });
 * const bundle = await mbm.getBundle('com.amazon.i18n.bundle');
 * const str = bundle.getMessage('helloWorld');
 *
 * @param options
 */
function createAsyncMessageBundleManager(options) {
    var remoteReaderOptions = {};
    if (options.resolveBundleUrl) {
        remoteReaderOptions.resolveBundleUrl = function (_options, fallbackItem) {
            return options.resolveBundleUrl(getUrlFromManifest(options.arbManifest, fallbackItem), fallbackItem.locale, fallbackItem.bundle);
        };
    }
    else {
        remoteReaderOptions.resolveBundleUrl = function (_options, fallbackItem) {
            var _a;
            var prefix = (_a = options.urlPrefix) !== null && _a !== void 0 ? _a : "";
            // this slash ensures we will request URLs relative to base URL
            if (prefix[0] !== "/") {
                prefix = "/" + prefix;
            }
            if (prefix && prefix[prefix.length - 1] !== "/") {
                prefix += "/";
            }
            return prefix + getUrlFromManifest(options.arbManifest, fallbackItem);
        };
    }
    if (options.resolveResourcePack) {
        remoteReaderOptions.resolveResourcePack = options.resolveResourcePack;
    }
    var remoteResourceReader = createFetchRemoteResourceReader(remoteReaderOptions);
    return new AsyncCombinedMessageBundleManager(new CallbackLocalizationContextProvider(options.localizationContextProvider), remoteResourceReader);
}

export { CallbackLocalizationContextProvider, DEFAULT_LOCALE_ATTRIBUTE, I18N_DOMAIN_ATTRIBUTE, LOCALE_ATTRIBUTE, LocalizationContextBuilder, LocalizationContextImplementation, MARKETPLACE_ATTRIBUTE, RetailLocalizationContext, RetailLocalizationContextBuilder, TIME_ZONE_ATTRIBUTE, createAsyncMessageBundleManager };
